import { motion, useReducedMotion } from 'framer-motion';
import { ArrowBigUp, ArrowRight, BarChart3, Check, CheckCheck, ChevronDown, FileText, Plus, ChevronLeft, ChevronRight, Facebook, Globe, Instagram, Linkedin, MessageCircle, Mic, PhoneOff, Send, Sparkles, Target, Twitter, Video, Lightbulb } from 'lucide-react';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useLocation } from 'wouter';

import { landingGradients } from '../constants';

import { getLanguageInfo, useLanguage, useLanguageSwitch, appendLocaleParam } from '@infra/i18n';
import { environment } from '@shared/config';
import { cn, getInitials } from '@shared/lib';
import { LanguageToggle } from '@shared/ui/layout';
import { useTranslation, type TranslationKey } from '@infra/i18n';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@shared/ui';
import { brand } from '@shared/brand';
import { brandColors, brandRgba, brandShadowRgba } from '@shared/constants';
import { HERO_PORTRAIT, PERSONAS, ROLEPLAY_INPUT_PLACEHOLDER_KEY, PERSONAS_VISIBLE_COUNT, PERSONAS_AUTO_INTERVAL_MS, JOURNEY_STEPS, JOURNEY_PERSONA_OPTIONS, JOURNEY_PERSONA_PAGE_COUNT, JOURNEY_GLASS_PANEL, JOURNEY_GLASS_SOFT, JOURNEY_CAROUSEL_INTERVAL_MS, JOURNEY_CAROUSEL_TRANSITION, PROGRESS_SUMMARY_STATS, PROGRESS_RECENT_SESSIONS, JOURNEY_STEP_COUNT, SCENARIO_MOCK, ROLEPLAY_SESSION, AI_FEEDBACK_MOCK } from '../constants/landing-page';

const Hero = brand.components.LandingHero ?? HeroSection;
const BrandNavbar = brand.components.LandingNavbar;
const BrandBody = brand.components.LandingBody;
const BrandFooter = brand.components.LandingFooter;

/** Demo avatars — local paths may 404; fallbacks keep UI intact */
export function LandingPage() {
  const [, navigate] = useLocation();

  const handleLogin = () => {
    if (!environment.loginUrl) {
      console.error('VITE_SSO_HOST is not configured.');
      return;
    }
    window.location.assign(appendLocaleParam(environment.loginUrl));
  };

  // Registration now uses our own page (not the FusionAuth hosted form).
  const handleRegister = () => navigate('/register');

  return (
    <div className="relative min-h-screen overflow-x-hidden bg-landing-page text-landing-ink font-sans selection:bg-tone-selection/40 selection:text-tone-on-selection antialiased">
      {/* ============ MOOD GRADIENT BASE (cool neutral) ============ */}
      <SoftGradientBackdrop />

      {BrandNavbar ? (
        <BrandLandingNavbar
          Component={BrandNavbar}
          onLogin={handleLogin}
          onRegister={environment.registrationEnabled ? handleRegister : undefined}
        />
      ) : (
        <Navbar onLogin={handleLogin} onRegister={handleRegister} />
      )}

      <main className="relative">
        {/* Block 1: fixed layout without parallax. */}
        <div className="landing-intro-block">
          <Hero
            onLogin={handleLogin}
            onRegister={environment.registrationEnabled ? handleRegister : undefined}
          />
        </div>

        {BrandBody ? (
          <BrandBody onRegister={environment.registrationEnabled ? handleRegister : undefined} />
        ) : (
          <>
            <ScrollIntoViewBlock>
              <PersonasSection />
            </ScrollIntoViewBlock>
            <ScrollIntoViewBlock>
              <TrainLikeRealLife />
            </ScrollIntoViewBlock>
            <CoachingJourneySection />
            <ScrollIntoViewBlock>
              <CTASection onRegister={handleRegister} />
            </ScrollIntoViewBlock>
          </>
        )}
      </main>

      {BrandFooter ? (
        <BrandFooter
          onLogin={handleLogin}
          onRegister={environment.registrationEnabled ? handleRegister : undefined}
        />
      ) : (
        <Footer />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* MOOD GRADIENT BACKDROP — cool neutral lavender-gray                 */
/* ------------------------------------------------------------------ */
function SoftGradientBackdrop() {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
      {/* Blue wash — strengthens sky-blue cast across the page */}
      <div
        className="absolute inset-0"
        style={{
          background: [
            `radial-gradient(120% 80% at 82% 8%, ${brandRgba('accentLight', 0.22)} 0%, transparent 50%)`,
            `radial-gradient(100% 70% at 70% 45%, ${brandRgba('accent', 0.16)} 0%, transparent 55%)`,
            `radial-gradient(90% 60% at 30% 20%, ${brandRgba('accentMid', 0.08)} 0%, transparent 50%)`,
          ].join(', '),
        }}
      />

      {/* ============ RIGHT-SIDE: BLUE + PINK DOMINANT AI HALO ============ */}

      {/* Large sky-blue orb (top-right) — primary AI signal, boosted */}
      <div
        className="absolute -top-24 -right-32 h-[820px] w-[820px] rounded-full opacity-90 blur-[140px]"
        style={{
          background:
            `radial-gradient(closest-side, ${brandRgba('accentLight', 0.52)}, ${brandRgba('accent', 0.28)} 40%, ${brandRgba('accentMid', 0.1)} 60%, transparent 78%)`,
        }}
      />
      {/* Secondary cyan orb (mid-right) — extra blue depth */}
      <div
        className="absolute top-[22%] -right-16 h-[520px] w-[520px] rounded-full opacity-75 blur-[120px]"
        style={{
          background:
            `radial-gradient(closest-side, ${brandRgba('accentLight', 0.38)}, ${brandRgba('accentLight', 0.18)} 50%, transparent 75%)`,
        }}
      />
      {/* Pink/rose orb (bottom-right) — pairs with blue for dichroic AI feel */}
      <div
        className="absolute -bottom-32 -right-20 h-[680px] w-[680px] rounded-full opacity-80 blur-[140px]"
        style={{
          background:
            `radial-gradient(closest-side, ${brandRgba('accentHighlight', 0.34)}, ${brandRgba('accentDeep', 0.18)} 50%, transparent 75%)`,
        }}
      />
      {/* Magenta-violet blend (mid-right) — iridescent connector */}
      <div
        className="absolute top-[38%] right-[8%] h-[460px] w-[460px] rounded-full opacity-70 blur-[140px]"
        style={{
          background:
            `radial-gradient(closest-side, ${brandRgba('accent', 0.34)}, ${brandRgba('accent', 0.12)} 50%, transparent 75%)`,
        }}
      />

      {/* ============ LEFT-SIDE: SOFTER VIOLET/INDIGO SUPPORT ============ */}

      {/* Soft violet (top-left) — balanced support */}
      <div
        className="absolute -top-20 -left-24 h-[520px] w-[520px] rounded-full opacity-60 blur-[140px]"
        style={{
          background:
            `radial-gradient(closest-side, ${brandRgba('accentMid', 0.28)}, ${brandRgba('accent', 0.12)} 50%, transparent 75%)`,
        }}
      />
      {/* Indigo accent (mid-left) — cool blue depth */}
      <div
        className="absolute top-[40%] -left-28 h-[440px] w-[440px] rounded-full opacity-60 blur-[140px]"
        style={{
          background:
            `radial-gradient(closest-side, ${brandRgba('accent', 0.28)}, ${brandRgba('accentLight', 0.08)} 55%, transparent 75%)`,
        }}
      />
      {/* Soft pearl center */}
      <div
        className="absolute top-1/3 left-1/2 h-[380px] w-[380px] -translate-x-1/2 rounded-full opacity-45 blur-[170px]"
        style={{
          background:
            `radial-gradient(closest-side, ${brandRgba('accentTint', 0.55)}, ${brandRgba('accentTintAlt', 0.35)} 50%, transparent 70%)`,
        }}
      />

      {/* ============ AI IRIDESCENT LAYERS ============ */}

      {/* Conic mesh — soft holographic wash (signature AI) */}
      <div
        className="absolute -inset-[20%] opacity-[0.22] blur-[100px]"
        style={{
          background:
            `conic-gradient(from 200deg at 72% 35%, ${brandRgba('accentLight', 0.55)}, ${brandRgba('accent', 0.45)}, ${brandRgba('accentMid', 0.5)}, ${brandRgba('accentDeep', 0.45)}, ${brandRgba('accentHighlight', 0.4)}, ${brandRgba('accentLight', 0.55)})`,
        }}
      />

      {/* Diagonal aurora sweep (upper) — blue-forward, peaks right */}
      <div
        className="absolute -inset-x-10 top-[16%] h-[440px] opacity-[0.38] blur-[80px]"
        style={{
          background:
            `linear-gradient(95deg, transparent 0%, ${brandRgba('accentLight', 0.4)} 18%, ${brandRgba('accent', 0.45)} 35%, ${brandRgba('accentMid', 0.55)} 52%, ${brandRgba('accentDeep', 0.55)} 70%, ${brandRgba('accentLight', 0.65)} 88%, transparent 100%)`,
        }}
      />
      {/* Lower aurora — pink → cyan blue ribbon */}
      <div
        className="absolute inset-x-0 top-[60%] h-[360px] opacity-[0.34] blur-[100px]"
        style={{
          background:
            `linear-gradient(85deg, transparent 0%, ${brandRgba('accent', 0.32)} 25%, ${brandRgba('accentHighlight', 0.48)} 48%, ${brandRgba('accentLight', 0.62)} 72%, ${brandRgba('accent', 0.45)} 88%, transparent 100%)`,
        }}
      />

      {/* Iridescent scan band — horizontal AI light streak */}
      <div
        className="absolute inset-x-0 top-[42%] h-px opacity-60"
        style={{
          background:
            `linear-gradient(90deg, transparent 0%, ${brandRgba('accentLight', 0)} 15%, ${brandRgba('accent', 0.55)} 35%, ${brandRgba('accentMid', 0.75)} 50%, ${brandRgba('accentDeep', 0.65)} 65%, ${brandRgba('accentLight', 0.55)} 85%, transparent 100%)`,
          boxShadow: `0 0 80px 20px ${brandShadowRgba('accent', 0.25)}, 0 0 120px 40px ${brandShadowRgba('accentLight', 0.15)}`,
        }}
      />
      {/* Soft glow above/below scan band */}
      <div
        className="absolute inset-x-0 top-[40%] h-[120px] opacity-[0.18] blur-[40px]"
        style={{
          background:
            `linear-gradient(90deg, transparent 5%, ${brandRgba('accentLight', 0.35)} 30%, ${brandRgba('accentMid', 0.4)} 50%, ${brandRgba('accentDeep', 0.35)} 70%, transparent 95%)`,
        }}
      />

      {/* Floating AI particles — subtle data-point sparkles */}
      <div
        className="absolute inset-0 opacity-[0.55]"
        style={{
          backgroundImage: [
            `radial-gradient(circle at 78% 18%, ${brandRgba('accentLight', 0.9)} 0, ${brandRgba('accentLight', 0.9)} 1.5px, transparent 2px)`,
            `radial-gradient(circle at 88% 42%, ${brandRgba('accent', 0.8)} 0, ${brandRgba('accent', 0.8)} 1px, transparent 1.5px)`,
            `radial-gradient(circle at 72% 65%, ${brandRgba('accentDeep', 0.7)} 0, ${brandRgba('accentDeep', 0.7)} 1px, transparent 1.5px)`,
            `radial-gradient(circle at 92% 78%, ${brandRgba('accentHighlight', 0.7)} 0, ${brandRgba('accentHighlight', 0.7)} 1.5px, transparent 2px)`,
            `radial-gradient(circle at 65% 28%, ${brandRgba('accentMid', 0.6)} 0, ${brandRgba('accentMid', 0.6)} 1px, transparent 1.5px)`,
            `radial-gradient(circle at 55% 52%, ${brandRgba('accentLight', 0.5)} 0, ${brandRgba('accentLight', 0.5)} 1px, transparent 1.5px)`,
          ].join(', '),
        }}
      />

      {/* ============ TECH OVERLAY: GRID + NOISE + TOP VIGNETTE ============ */}

      {/* Hairline grid — slightly more visible for AI tech feel */}
      <div
        className="absolute inset-0 opacity-[0.045]"
        style={{
          backgroundImage:
            `linear-gradient(to right, ${brandRgba('accent', 0.6)} 1px, transparent 1px), linear-gradient(to bottom, ${brandRgba('accent', 0.6)} 1px, transparent 1px)`,
          backgroundSize: '56px 56px',
        }}
      />
      {/* Diagonal micro-lines — neural mesh hint */}
      <div
        className="absolute inset-0 opacity-[0.025]"
        style={{
          backgroundImage:
            `repeating-linear-gradient(125deg, transparent, transparent 80px, ${brandRgba('accentLight', 0.4)} 80px, ${brandRgba('accentLight', 0.4)} 81px)`,
        }}
      />
      {/* Subtle noise overlay */}
      <div
        className="absolute inset-0 opacity-[0.045] mix-blend-overlay"
        style={{
          backgroundImage: `radial-gradient(${brandRgba('accent', 0.45)} 1px, transparent 1px)`,
          backgroundSize: '24px 24px',
        }}
      />
      {/* Top white vignette — keeps navbar legibility */}
      <div
        className="absolute inset-x-0 top-0 h-40 opacity-55"
        style={{
          background: `linear-gradient(to bottom, ${brandRgba('surfaceCard', 0.75)}, ${brandRgba('surfaceCard', 0)})`,
        }}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* SCROLL: the lower block slides into position without parallax.     */
/* ------------------------------------------------------------------ */
function ScrollIntoViewBlock({
  children,
  className,
  delay = 0,
}: {
  children: ReactNode;
  className?: string;
  delay?: number;
}) {
  const reduceMotion = useReducedMotion();

  if (reduceMotion) {
    return <div className={className}>{children}</div>;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.12, margin: '0px 0px -5% 0px' }}
      transition={{
        type: 'spring',
        stiffness: 160,
        damping: 26,
        mass: 0.65,
        delay,
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/* NAVBAR                                                              */
/* ------------------------------------------------------------------ */
function Navbar({ onLogin, onRegister }: { onLogin: () => void; onRegister: () => void }) {
  const { t } = useTranslation();

  return (
    <header className="relative z-50 w-full pt-4">
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between px-2 py-2">
          {/* Brand */}
          <a href="/" className="flex items-center gap-2.5">
            <div className={`relative grid h-10 w-10 place-items-center rounded-2xl bg-gradient-to-br from-[var(--brand-accent-light)] via-[var(--brand-accent-mid)] to-[var(--brand-accent-highlight)] shadow-[0_10px_24px_-6px_rgba(var(--brand-rgb-accent-mid),calc(var(--shadow-strength)*0.6))]`}>
              <brand.components.LogoGlyph className="h-5 w-5 text-tone-on-accent" />
              {/* inner gloss */}
              <div className="absolute inset-x-1 top-1 h-3 rounded-full bg-tone-glass/40 blur-[2px]" />
            </div>
            <div className="leading-tight">
              <div className={`text-[17px] font-extrabold tracking-tight text-[var(--brand-ink)]`}>
                {t('landing:brand.name')}
              </div>
              <div className={`text-[10px] font-medium text-[var(--brand-text-tertiary)]`}>
                {t('landing:brand.tagline')}
              </div>
            </div>
          </a>

          {/* Nav links */}
          <nav className="hidden items-center gap-9 lg:flex">
            {['features', 'personas', 'solutions', 'pricing'].map((key) => (
              <a
                key={key}
                href={`#${key}`}
                className={`text-[14px] font-medium text-[var(--brand-text-deep)] transition-colors hover:text-[var(--brand-accent-deep)]`}
              >
                {t(`landing:nav.${key}`)}
              </a>
            ))}
            <button
              type="button"
              className={`inline-flex items-center gap-1 text-[14px] font-medium text-[var(--brand-text-deep)] transition-colors hover:text-[var(--brand-accent-deep)]`}
            >
              {t('landing:nav.resources')} <ChevronDown className="h-3.5 w-3.5" />
            </button>
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <LanguageToggle />
            <GlassButton onClick={onLogin} className="hidden px-5 py-2 text-[13px] sm:inline-flex">
              {t('landing:actions.login')}
            </GlassButton>
            {environment.registrationEnabled && (
              <GradientButton onClick={onRegister} className="px-5 py-2 text-[13px]">
                {t('landing:actions.register')}
                <ArrowRight className="ml-1 h-4 w-4" />
              </GradientButton>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}

/** Feeds the pack's navbar the same links and language switch as the default navbar. */
function BrandLandingNavbar({
  Component,
  onLogin,
  onRegister,
}: {
  Component: NonNullable<typeof BrandNavbar>;
  onLogin: () => void;
  onRegister?: () => void;
}) {
  const { t } = useTranslation();
  const navLinks = ['features', 'personas', 'solutions', 'pricing'].map((key) => ({
    href: `#${key}`,
    label: t(`landing:nav.${key}`),
  }));

  return (
    <Component
      onLogin={onLogin}
      onRegister={onRegister}
      languageSwitch={<LanguageToggle />}
      navLinks={navLinks}
    />
  );
}

/* ------------------------------------------------------------------ */
/* HERO                                                                */
/* ------------------------------------------------------------------ */
function HeroSection({ onLogin }: { onLogin: () => void }) {
  const { t } = useTranslation();

  return (
    <section className="relative pt-6 pb-4 lg:pt-8">
      <div className="container mx-auto px-4 md:px-6">
        <div className="relative grid grid-cols-1 items-center gap-10 lg:grid-cols-[1fr_1.3fr] lg:gap-12 xl:grid-cols-[1fr_1.45fr]">
          {/* LEFT: Copy */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
            className="relative"
          >
            {/* Badge */}
            <div className={`inline-flex items-center gap-2 rounded-full border border-tone-glass/70 bg-tone-glass/65 px-4 py-1.5 text-[10.5px] font-bold uppercase tracking-[0.12em] text-[var(--brand-accent-deep)] shadow-[0_8px_20px_-8px_rgba(var(--brand-rgb-accent),calc(var(--shadow-strength)*0.35))] backdrop-blur-xl`}>
              <span className={`grid h-4 w-4 place-items-center rounded-full bg-gradient-to-br from-[var(--brand-accent-light)] to-[var(--brand-accent-soft)]`}>
                <Sparkles className="h-2.5 w-2.5 text-tone-on-accent" />
              </span>
              {t('landing:hero.badge')}
            </div>

            {/* Headline */}
            <h1 className={`mt-7 text-[44px] font-extrabold leading-[1.05] tracking-[-0.02em] text-[var(--brand-ink)] md:text-[56px] lg:text-[64px]`}>
              {t('landing:hero.titleLine1')}{' '}
              <span
                className="bg-clip-text text-transparent"
                style={{
                  backgroundImage: landingGradients.brandInline,
                }}
              >
                {t('landing:hero.titleHighlighted')}
              </span>
              <br />
              {t('landing:hero.titleLine2')}
            </h1>

            <p className={`mt-6 max-w-md text-[15px] leading-relaxed text-[var(--brand-text-deep)]`}>
              {t('landing:hero.subtitle')}
            </p>

            {/* CTAs */}
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <GradientButton onClick={onLogin} className="px-7 py-3.5 text-[14px]">
                {t('landing:hero.primaryCta')}
                <ArrowRight className="ml-1.5 h-4 w-4" />
              </GradientButton>
              <GlassButton className="gap-2 px-7 py-3.5 text-[14px]">
                {t('landing:hero.secondaryCta')}
              </GlassButton>
            </div>
          </motion.div>

          {/* RIGHT: Glass roleplay card with floating chips */}
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: 'easeOut', delay: 0.15 }}
            className="relative mx-auto w-full max-w-[650px] lg:max-w-[750px]"
          >
            <RoleplayHeroCard />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function TypewriterPlaceholder({
  text = '',
  charDelayMs = 65,
  pauseAtEndMs = 1800,
}: {
  text?: string;
  charDelayMs?: number;
  pauseAtEndMs?: number;
}) {
  const { t } = useTranslation();
  const translatedText =
    text || t(ROLEPLAY_INPUT_PLACEHOLDER_KEY, { defaultValue: 'Your response...' });
  const [displayed, setDisplayed] = useState('');

  useEffect(() => {
    let index = 0;
    let timeoutId = 0;
    let cancelled = false;

    const tick = () => {
      if (cancelled) return;

      if (index < translatedText.length) {
        index += 1;
        setDisplayed(translatedText.slice(0, index));
        timeoutId = window.setTimeout(tick, charDelayMs);
        return;
      }

      timeoutId = window.setTimeout(() => {
        if (cancelled) return;
        index = 0;
        setDisplayed('');
        timeoutId = window.setTimeout(tick, charDelayMs);
      }, pauseAtEndMs);
    };

    tick();

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [translatedText, charDelayMs, pauseAtEndMs]);

  return (
    <div className="flex min-w-0 flex-1 items-center" aria-label={translatedText} role="textbox">
      <span className={`truncate text-[13px] text-[var(--brand-text-muted)]`}>
        {displayed}
        <span
          aria-hidden
          className="ml-px inline-block h-[14px] w-[2px] translate-y-px animate-pulse bg-tone-caret/80 align-middle"
        />
      </span>
    </div>
  );
}

function RoleplayHeroCard() {
  const { t } = useTranslation();

  return (
    <div className="relative">
      {/* Big halo (very soft) */}
      <div
        className="absolute -inset-12 -z-10 rounded-[3.5rem] blur-3xl"
        style={{
          background:
            `radial-gradient(60% 60% at 50% 50%, ${brandRgba('accentMid', 0.3)} 0%, ${brandRgba('accent', 0.18)} 45%, ${brandRgba('surfaceCard', 0)} 70%)`,
        }}
      />

      {/* MAIN GLASS CARD — borderless, AI iridescent shadow stack (softened) */}
      <div
        className="relative overflow-hidden rounded-[28px] p-1 backdrop-blur-2xl"
        style={{
          boxShadow: [
            // Ambient depth — soft indigo (lighter)
            `0 40px 90px -30px ${brandShadowRgba('accent', 0.22)}`,
            // Mid violet AI glow (lighter)
            `0 28px 60px -24px ${brandShadowRgba('accentMid', 0.18)}`,
            // Magenta tint for iridescent feel (lighter)
            `0 18px 40px -22px ${brandShadowRgba('accentDeep', 0.12)}`,
            // Sky/cyan accent (lighter)
            `0 14px 32px -22px ${brandShadowRgba('accentLight', 0.1)}`,
            // Subtle key shadow for grounding
            `0 6px 18px -10px ${brandShadowRgba('shadowDeep', 0.1)}`,
            // Inner top highlight — soft glossy edge
            `inset 0 1px 0 ${brandShadowRgba('surfaceCard', 0.45)}`,
            // Inner iridescent rim — barely visible 1px line
            `inset 0 0 0 1px ${brandRgba('accentMid', 0.1)}`,
          ].join(', '),
        }}
      >
        {/* Outer iridescent halo — very subtle conic rim */}
        <div
          aria-hidden
          className="pointer-events-none absolute -inset-px rounded-[28px] opacity-40"
          style={{
            background:
              `conic-gradient(from 140deg at 50% 50%, ${brandRgba('accent', 0.35)}, ${brandRgba('accentMid', 0.35)}, ${brandRgba('accentDeep', 0.25)}, ${brandRgba('accentLight', 0.3)}, ${brandRgba('accent', 0.35)})`,
            mask: `linear-gradient(${brandColors.maskOpaque} 0 0) content-box, linear-gradient(${brandColors.maskOpaque} 0 0)`,
            WebkitMask: `linear-gradient(${brandColors.maskOpaque} 0 0) content-box, linear-gradient(${brandColors.maskOpaque} 0 0)`,
            WebkitMaskComposite: 'xor',
            maskComposite: 'exclude',
            padding: 1,
          }}
        />

        {/* Portrait area — fills the card directly, no white padding */}
        <div className="relative aspect-[5/4] overflow-hidden rounded-[28px] bg-gradient-to-br from-tone-card-tint to-tone-card-tint-warm">
          <img
            src={HERO_PORTRAIT}
            alt={t('landing:a11y.personaPortrait')}
            className="absolute inset-0 h-full w-full object-cover"
          />

          {/* Subtle vignette to keep overlays legible */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                `linear-gradient(180deg, ${brandRgba('shadowDeep', 0.05)} 0%, ${brandRgba('shadowDeep', 0)} 18%, ${brandRgba('shadowDeep', 0)} 70%, ${brandRgba('shadowDeep', 0.06)} 100%)`,
            }}
          />

          {/* Top-left Live Roleplay badge — dark teal as in screenshot */}
          <div className={`absolute left-5 top-5 inline-flex items-center gap-1.5 rounded-full border border-tone-glass/15 bg-[color-mix(in_srgb,var(--brand-success)_90%,transparent)] px-3 py-1.5 text-[11px] font-semibold text-tone-on-accent shadow-[0_4px_12px_-2px_rgba(var(--brand-rgb-success),calc(var(--shadow-strength)*0.45))] backdrop-blur-md`}>
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-tone-positive-bright opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-tone-positive-bright" />
            </span>
            {t('landing:hero.liveBadge')}
          </div>

          {/* FLOATING CHIPS COLUMN — overlay on the right side, evenly spaced */}
          <div className="absolute bottom-[132px] right-5 top-5 hidden w-[170px] flex-col md:flex">
            <FloatingChip delay={0.4} className="w-full">
              <div className="min-w-0 flex-1">
                <div className={`text-[10px] font-medium leading-tight text-[var(--brand-text-tertiary)]`}>
                  {t('landing:hero.trustLevel')}
                </div>
                <div className={`mt-0.5 text-lg font-extrabold leading-none text-[var(--brand-ink)]`}>72%</div>
              </div>
              <CircularRing value={72} />
            </FloatingChip>

            <div className="h-2.5" />

            <FloatingChip delay={0.55} className="w-full">
              <div className="min-w-0 flex-1">
                <div className={`text-[10px] font-medium leading-tight text-[var(--brand-text-tertiary)]`}>
                  {t('landing:hero.moodLabel')}
                </div>
                <div className={`mt-0.5 text-[14px] font-extrabold leading-none text-[var(--brand-ink)]`}>
                  {t('landing:hero.moodCautious')}
                </div>
              </div>
              <div
                className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-base shadow-inner"
                style={{
                  background: `radial-gradient(circle at 30% 30%, ${brandColors.rewardHighlight} 0%, ${brandColors.rewardBase} 100%)`,
                }}
              >
                <span className="text-[15px] leading-none">😐</span>
              </div>
            </FloatingChip>

            <div className="h-2.5" />

            <FloatingChip delay={0.7} className="w-full">
              <div className="min-w-0 flex-1">
                <div className={`text-[10px] font-medium leading-tight text-[var(--brand-text-tertiary)]`}>
                  {t('landing:hero.conversationTime')}
                </div>
                <div className={`mt-0.5 text-[14px] font-extrabold leading-none text-[var(--brand-ink)]`}>
                  08:42
                </div>
              </div>
            </FloatingChip>

            {/* Flexible spacer pushes the next chip to the bottom */}
            <div className="flex-1 min-h-[14px]" />

            <FloatingChip delay={0.85} className="w-full flex-col items-stretch !gap-2.5">
              <div className="flex items-center gap-2.5">
                <div
                  className={`relative grid h-10 w-10 shrink-0 place-items-center rounded-full shadow-[inset_0_-2px_4px_rgba(var(--brand-rgb-warning),calc(var(--shadow-strength)*0.25)),0_2px_4px_rgba(var(--brand-rgb-reward-base),calc(var(--shadow-strength)*0.3))]`}
                  style={{
                    background:
                      `radial-gradient(circle at 30% 25%, ${brandColors.rewardHighlight} 0%, ${brandColors.rewardMid} 45%, ${brandColors.rewardBase} 100%)`,
                  }}
                >
                  <Lightbulb
                    className={`h-[18px] w-[18px] text-tone-on-accent drop-shadow-[0_1px_1px_rgba(var(--brand-rgb-warning),calc(var(--shadow-strength)*0.4))]`}
                    fill="white"
                    strokeWidth={2}
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <div className={`text-[10px] font-medium leading-tight text-[var(--brand-text-tertiary)]`}>
                    {t('landing:hero.nextObjection')}
                  </div>
                  <div className={`mt-0.5 text-[13px] font-extrabold leading-tight text-[var(--brand-ink)]`}>
                    {t('landing:hero.priceConcern')}
                  </div>
                </div>
              </div>
              <a
                href="#"
                className={`inline-flex items-center justify-center gap-1 rounded-xl bg-tone-glass/85 py-1.5 text-[11px] font-semibold text-[var(--brand-accent-deep)] shadow-[inset_0_0_0_1px_rgba(var(--brand-rgb-accent-deep),0.1),0_1px_2px_rgba(var(--brand-rgb-shadow-deep),calc(var(--shadow-strength)*0.04))] backdrop-blur-md transition-all hover:bg-tone-surface hover:text-[var(--brand-accent-deep-hover)]`}
              >
                {t('landing:hero.viewTips')} <ArrowRight className="h-3 w-3" />
              </a>
            </FloatingChip>
          </div>

          {/* Customer chat bubble — positioned bottom-left, above input bar */}
          <div className="absolute bottom-[132px] left-5 max-w-[55%]">
            <div className={`inline-flex flex-col items-start rounded-2xl rounded-bl-md border border-tone-glass/80 bg-tone-glass/90 px-3.5 py-2.5 shadow-[0_10px_30px_-14px_rgba(var(--brand-rgb-shadow-deep),calc(var(--shadow-strength)*0.22)),0_2px_6px_calc(-2px)_rgba(var(--brand-rgb-shadow-deep),calc(var(--shadow-strength)*0.06))] backdrop-blur-xl`}>
              <div className={`mb-1.5 inline-flex items-center gap-1 rounded-full bg-[var(--brand-success)] px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-tone-on-positive`}>
                <span className="h-1 w-1 rounded-full bg-tone-on-positive-muted" />{' '}
                {t('landing:hero.customerLabel')}
              </div>
              <div className={`text-[13px] leading-snug text-[var(--brand-ink)]`}>
                {t('landing:hero.customerQuoteLine1')}
                <br />
                {t('landing:hero.customerQuoteLine2')}
              </div>
              <div className="mt-1.5 flex items-center gap-1.5">
                <span className={`h-2 w-2 animate-pulse rounded-full bg-[var(--brand-accent)] shadow-[0_0_6px_rgba(var(--brand-rgb-accent),calc(var(--shadow-strength)*0.45))]`} />
                <span
                  className={`h-2 w-2 animate-pulse rounded-full bg-[var(--brand-accent)] shadow-[0_0_6px_rgba(var(--brand-rgb-accent),calc(var(--shadow-strength)*0.45))]`}
                  style={{ animationDelay: '150ms' }}
                />
                <span
                  className={`h-2 w-2 animate-pulse rounded-full bg-[var(--brand-accent)] shadow-[0_0_6px_rgba(var(--brand-rgb-accent),calc(var(--shadow-strength)*0.45))]`}
                  style={{ animationDelay: '300ms' }}
                />
              </div>
            </div>
          </div>

          {/* Bottom control stack — input bar + call buttons in one cohesive block */}
          <div className="absolute inset-x-5 bottom-4 flex flex-col items-center gap-2.5">
            {/* Input bar — 60% width on large screens, full width on mobile */}
            <div className={`flex w-full items-center gap-2 rounded-full border border-tone-glass/80 bg-tone-glass/90 px-4 py-2 shadow-[0_10px_30px_-12px_rgba(var(--brand-rgb-shadow-deep),calc(var(--shadow-strength)*0.18)),0_2px_6px_calc(-2px)_rgba(var(--brand-rgb-shadow-deep),calc(var(--shadow-strength)*0.06))] backdrop-blur-xl lg:w-3/5`}>
              <TypewriterPlaceholder />
              <button
                type="button"
                className={`grid h-7 w-7 place-items-center rounded-full text-[var(--brand-text-muted)] transition-colors hover:bg-tone-card-edge`}
                aria-label={t('landing:hero.voiceButtonLabel')}
              >
                <Mic className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                className={`grid h-8 w-8 place-items-center rounded-full text-tone-on-accent shadow-[0_6px_14px_-4px_rgba(var(--brand-rgb-accent),calc(var(--shadow-strength)*0.55))]`}
                style={{
                  background: `linear-gradient(135deg, ${brandColors.accent} 0%, ${brandColors.accentMid} 60%, ${brandColors.accentSoft} 100%)`,
                }}
                aria-label={t('landing:hero.sendButtonLabel')}
              >
                <Send className="h-3.5 w-3.5" />
              </button>
            </div>

            {/* Call controls row — same wrapper as input bar */}
            <div className="flex items-center justify-center gap-3">
              <CallButton tone="neutral" icon={<Mic className="h-4 w-4" />} />
              <CallButton tone="neutral" icon={<Video className="h-4 w-4" />} />
              <CallButton tone="neutral" icon={<MessageCircle className="h-4 w-4" />} />
              <CallButton tone="danger" icon={<PhoneOff className="h-4 w-4" />} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CallButton({ tone, icon }: { tone: 'neutral' | 'danger'; icon: ReactNode }) {
  return (
    <button
      type="button"
      className={cn(
        'grid h-11 w-11 place-items-center rounded-full border transition-all hover:-translate-y-0.5',
        tone === 'neutral'
          ? `border-tone-glass/90 bg-tone-surface text-[var(--brand-text-deep)] shadow-[0_6px_16px_-6px_rgba(var(--brand-rgb-shadow-deep),calc(var(--shadow-strength)*0.18)),0_2px_4px_calc(-2px)_rgba(var(--brand-rgb-shadow-deep),calc(var(--shadow-strength)*0.1))] hover:bg-tone-surface hover:shadow-[0_10px_24px_-8px_rgba(var(--brand-rgb-shadow-deep),calc(var(--shadow-strength)*0.25))]`
          : `border-tone-critical-bright/30 text-tone-on-accent shadow-[0_10px_24px_-6px_rgba(var(--brand-rgb-danger-light),calc(var(--shadow-strength)*0.55)),0_4px_10px_calc(-3px)_rgba(var(--brand-rgb-danger-light),calc(var(--shadow-strength)*0.4))] hover:shadow-[0_14px_30px_-8px_rgba(var(--brand-rgb-danger-light),calc(var(--shadow-strength)*0.7))]`
      )}
      style={
        tone === 'danger'
          ? {
              background: `linear-gradient(135deg, ${brandColors.dangerButtonStart} 0%, ${brandColors.dangerLight} 100%)`,
            }
          : undefined
      }
    >
      {icon}
    </button>
  );
}

function FloatingChip({
  children,
  className,
  delay = 0,
}: {
  children: ReactNode;
  className?: string;
  delay?: number;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, x: 30, y: 10 }}
      animate={{ opacity: 1, x: 0, y: 0 }}
      transition={{ duration: 0.6, delay, ease: 'easeOut' }}
      className={cn(
        `flex items-center gap-3 rounded-2xl border border-tone-glass/70 bg-tone-glass/80 px-3.5 py-3.5 shadow-[0_10px_28px_-14px_rgba(var(--brand-rgb-accent),calc(var(--shadow-strength)*0.3)),0_2px_6px_calc(-2px)_rgba(var(--brand-rgb-shadow-deep),calc(var(--shadow-strength)*0.06)),inset_0_1px_0_rgba(var(--brand-rgb-surface-card),calc(var(--shadow-strength)*0.7))] backdrop-blur-xl`,
        className
      )}
    >
      {children}
    </motion.div>
  );
}

function CircularRing({ value }: { value: number }) {
  const radius = 15;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (value / 100) * circumference;
  return (
    <svg width={38} height={38} viewBox="0 0 40 40" className="-rotate-90 shrink-0">
      <circle cx={20} cy={20} r={radius} stroke={brandColors.elevatedAlt} strokeWidth={4} fill="none" />
      <circle
        cx={20}
        cy={20}
        r={radius}
        stroke="url(#ringGrad)"
        strokeWidth={4}
        strokeLinecap="round"
        fill="none"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
      />
      <defs>
        <linearGradient id="ringGrad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={brandColors.progressRingStart} />
          <stop offset="100%" stopColor={brandColors.progressRingEnd} />
        </linearGradient>
      </defs>
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/* PERSONAS                                                            */
/* ------------------------------------------------------------------ */
/** Default cards, or the pack's own list matched by name key with its photo and age. */
function brandPersonas(): typeof PERSONAS {
  const override = brand.landingPersonas;
  if (!override) return PERSONAS;
  return override.flatMap((o) => {
    const base = PERSONAS.find((p) => p.nameKey === o.nameKey);
    if (!base) {
      if (import.meta.env.DEV) console.warn(`Brand persona ${o.nameKey} matches no landing card`);
      return [];
    }
    return [{ ...base, photo: o.photo, age: o.age, coolPhoto: false }];
  });
}

/** Pack photo for a name key, else the default one. */
function brandPhoto(list: typeof brand.landingPersonas, nameKey: string, fallback: string): string {
  return list?.find((o) => o.nameKey === nameKey)?.photo ?? fallback;
}

/** Journey picker options with the pack's photo and age where it names them. */
function brandJourneyOptions(): typeof JOURNEY_PERSONA_OPTIONS {
  const override = brand.landingJourneyPersonas;
  if (!override) return JOURNEY_PERSONA_OPTIONS;
  return JOURNEY_PERSONA_OPTIONS.map((p) => {
    const o = override.find((x) => x.nameKey === p.nameKey);
    return o ? { ...p, photo: o.photo, age: o.age } : p;
  });
}

function PersonasSection() {
  const { t } = useTranslation();
  const personas = brandPersonas();
  const maxIndex = Math.max(0, personas.length - PERSONAS_VISIBLE_COUNT);
  const canSlide = maxIndex > 0;
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const goNext = () => setIndex((i) => (i >= maxIndex ? 0 : i + 1));
  const goPrev = () => setIndex((i) => (i <= 0 ? maxIndex : i - 1));

  // Auto-advance every 3.5s; pauses while user hovers the carousel
  useEffect(() => {
    if (!canSlide || paused) return;
    const timer = window.setInterval(() => {
      setIndex((i) => (i >= maxIndex ? 0 : i + 1));
    }, PERSONAS_AUTO_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [canSlide, paused, maxIndex]);

  const personaPages = Array.from({ length: maxIndex + 1 }, (_, page) =>
    personas.slice(page, page + PERSONAS_VISIBLE_COUNT)
  );

  return (
    <section id="personas" className="py-4">
      <div className="container mx-auto px-4 md:px-6">
        <div className="relative overflow-hidden rounded-3xl border border-tone-glass/60 bg-tone-glass/55 p-6 shadow-sm backdrop-blur-xl md:p-8">
          {/* Iridescent header glow — signature AI element */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 top-0 h-px"
            style={{
              background:
                `linear-gradient(90deg, transparent 0%, ${brandRgba('accent', 0.5)} 25%, ${brandRgba('accentMid', 0.7)} 50%, ${brandRgba('accentDeep', 0.5)} 75%, transparent 100%)`,
            }}
          />
          <div className="mb-6 flex items-end justify-between">
            <div>
              <h2 className="text-2xl font-extrabold tracking-tight text-tone-ink-strong md:text-3xl">
                {t('landing:personas.title')}
              </h2>
              <p className="mt-1.5 text-sm text-tone-ink-muted">{t('landing:personas.subtitle')}</p>
            </div>
            <a
              href="#"
              className="hidden items-center gap-1 text-sm font-semibold text-tone-accent-ink hover:text-tone-accent-ink-deep md:inline-flex"
            >
              {t('landing:personas.viewAll')} <ArrowRight className="h-3.5 w-3.5" />
            </a>
          </div>

          <div
            className="relative"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            onFocusCapture={() => setPaused(true)}
            onBlurCapture={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
                setPaused(false);
              }
            }}
          >
            {canSlide && (
              <>
                <button
                  type="button"
                  aria-label={t('landing:a11y.previousPersonas')}
                  onClick={goPrev}
                  className={`absolute -left-3 top-1/2 z-10 -translate-y-1/2 rounded-full border border-tone-glass/70 bg-tone-glass/80 p-2 shadow-md backdrop-blur-md transition-all hover:bg-tone-surface hover:shadow-[0_8px_20px_-6px_rgba(var(--brand-rgb-accent-deep),calc(var(--shadow-strength)*0.35))]`}
                >
                  <ChevronLeft className="h-4 w-4 text-tone-ink-body" />
                </button>
                <button
                  type="button"
                  aria-label={t('landing:a11y.nextPersonas')}
                  onClick={goNext}
                  className={`absolute -right-3 top-1/2 z-10 -translate-y-1/2 rounded-full border border-tone-glass/70 bg-tone-glass/80 p-2 shadow-md backdrop-blur-md transition-all hover:bg-tone-surface hover:shadow-[0_8px_20px_-6px_rgba(var(--brand-rgb-accent-deep),calc(var(--shadow-strength)*0.35))]`}
                >
                  <ChevronRight className="h-4 w-4 text-tone-ink-body" />
                </button>
              </>
            )}

            <div className="overflow-hidden">
              <motion.div
                className="flex"
                animate={{ x: `-${index * 100}%` }}
                transition={{ duration: 0.55, ease: [0.25, 0.1, 0.25, 1] }}
              >
                {personaPages.map((page, pageIdx) => (
                  <div
                    key={pageIdx}
                    className="grid w-full shrink-0 grid-cols-2 gap-4 lg:grid-cols-4"
                  >
                    {page.map((p) => (
                      <PersonaCard key={p.nameKey} persona={p} />
                    ))}
                  </div>
                ))}
              </motion.div>
            </div>

            {canSlide && (
              <div className="mt-4 flex items-center justify-center gap-1.5">
                {Array.from({ length: maxIndex + 1 }, (_, i) => (
                  <button
                    key={i}
                    type="button"
                    aria-label={`Go to personas slide ${i + 1}`}
                    aria-current={i === index ? 'true' : undefined}
                    onClick={() => setIndex(i)}
                    className={cn(
                      'h-1.5 rounded-full transition-all',
                      i === index
                        ? `w-5 bg-gradient-to-r from-[var(--brand-accent)] via-[var(--brand-accent-mid)] to-[var(--brand-accent-deep)]`
                        : 'w-1.5 bg-tone-indicator-idle/80 hover:bg-tone-accent-line'
                    )}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function PersonaCard({ persona }: { persona: (typeof PERSONAS)[number] }) {
  const { t } = useTranslation();

  return (
    <motion.div
      whileHover={{ y: -6 }}
      transition={{ duration: 0.25 }}
      className={cn(
        `group relative overflow-hidden rounded-2xl border border-tone-glass/70 bg-tone-glass/75 p-2.5 shadow-[0_4px_16px_-6px_rgba(var(--brand-rgb-shadow-deep),calc(var(--shadow-strength)*0.06))] backdrop-blur-xl transition-all hover:shadow-[0_18px_40px_-12px_rgba(var(--brand-rgb-accent-mid),calc(var(--shadow-strength)*0.35))]`,
        persona.active &&
          `shadow-[0_22px_50px_-14px_rgba(var(--brand-rgb-accent-mid),calc(var(--shadow-strength)*0.55)),0_0_0_2px_rgba(var(--brand-rgb-accent-mid),0.2)]`
      )}
    >
      {/* Iridescent gradient ring for active card */}
      {persona.active && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 rounded-2xl p-[2px]"
          style={{
            background:
              `linear-gradient(135deg, ${brandColors.accent} 0%, ${brandColors.accentMid} 35%, ${brandColors.accentDeep} 65%, ${brandColors.accentLight} 100%)`,
            WebkitMask: `linear-gradient(${brandColors.maskOpaque} 0 0) content-box, linear-gradient(${brandColors.maskOpaque} 0 0)`,
            WebkitMaskComposite: 'xor',
            maskComposite: 'exclude',
          }}
        />
      )}
      <div className={cn('absolute inset-0 -z-10 bg-gradient-to-br', persona.cardTint)} />
      <div className="relative aspect-[4/5] overflow-hidden rounded-xl">
        <img
          src={persona.photo}
          alt={t(persona.nameKey)}
          className={cn(
            'h-full w-full object-cover transition-transform duration-500 group-hover:scale-105',
            persona.coolPhoto && '[filter:saturate(0.8)_hue-rotate(-16deg)_brightness(1.02)]'
          )}
        />
        {/* Cool wash — shifts a warm-lit portrait toward the sky palette */}
        {persona.coolPhoto && (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 bg-gradient-to-br from-tone-photo-wash/35 via-tone-photo-wash-mid/15 to-tone-photo-wash-end/25 mix-blend-soft-light"
          />
        )}
        {/* Top vignette to keep tone badge readable */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-16"
          style={{
            background: `linear-gradient(180deg, ${brandRgba('shadowDeep', 0.22)} 0%, transparent 100%)`,
          }}
        />
        <div className="absolute right-2 top-2 inline-flex items-center rounded-full border border-tone-glass/70 bg-tone-glass/90 px-2 py-0.5 text-[10px] font-semibold text-tone-ink-body shadow-sm backdrop-blur-md">
          {t(persona.toneKey)}
        </div>
      </div>
      <div className="relative px-1.5 py-3">
        <div className="flex items-center gap-1">
          <h3 className="text-[15px] font-bold text-tone-ink-strong">{t(persona.nameKey)}</h3>
          {persona.active && (
            <span className="ml-auto inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-tone-accent-ink">
              <span className="h-1 w-1 rounded-full bg-tone-accent-icon" /> {t('landing:personas.active')}
            </span>
          )}
        </div>
        <div className="mt-1 text-[11px] text-tone-ink-muted">
          <span className="font-medium text-tone-ink-body">
            {persona.age} {t('landing:personas.yearsOld')}
          </span>{' '}
          · {t(persona.cityKey)}
        </div>
        <div className="mt-2.5 flex flex-wrap gap-1">
          <Pill>{t(persona.trait1Key)}</Pill>
          <Pill>{t(persona.trait2Key)}</Pill>
        </div>
      </div>
    </motion.div>
  );
}

function Pill({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-tone-glass/70 bg-tone-glass/70 px-2 py-0.5 text-[10px] font-semibold text-tone-ink-secondary backdrop-blur-md">
      <span
        className="h-1.5 w-1.5 rounded-full"
        style={{
          background: `linear-gradient(135deg, ${brandColors.accent} 0%, ${brandColors.accentMid} 50%, ${brandColors.accentDeep} 100%)`,
        }}
      />
      {children}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* TRAIN LIKE REAL LIFE                                                */
/* ------------------------------------------------------------------ */
function TrainLikeRealLife() {
  const { t } = useTranslation();

  const features = [
    {
      titleKey: 'landing:features.card.realConversations.title',
      descKey: 'landing:features.card.realConversations.description',
      icon: (
        <MessageCircle
          className="h-[22px] w-[22px]"
          strokeWidth={2}
          fill="currentColor"
          fillOpacity={0.15}
        />
      ),
      iconBg: 'bg-tone-card-tint/80',
      iconColor: 'text-tone-accent-icon',
    },
    {
      titleKey: 'landing:features.card.smartFeedback.title',
      descKey: 'landing:features.card.smartFeedback.description',
      icon: (
        <div className="relative">
          <BarChart3 className="h-[22px] w-[22px]" strokeWidth={2.2} />
          <Sparkles className="absolute -right-1 -top-1 h-2.5 w-2.5" />
        </div>
      ),
      iconBg: 'bg-tone-card-tint/70',
      iconColor: 'text-tone-accent-icon',
    },
    {
      titleKey: 'landing:features.card.improveFaster.title',
      descKey: 'landing:features.card.improveFaster.description',
      icon: (
        <ArrowBigUp
          className="h-[24px] w-[24px]"
          strokeWidth={2}
          fill="currentColor"
          fillOpacity={0.15}
        />
      ),
      iconBg: 'bg-tone-card-tint/80',
      iconColor: 'text-tone-accent-icon',
    },
    {
      titleKey: 'landing:features.card.trackProgress.title',
      descKey: 'landing:features.card.trackProgress.description',
      icon: <Target className="h-[22px] w-[22px]" strokeWidth={2.4} />,
      iconBg: 'bg-tone-card-tint-strong/70',
      iconColor: 'text-tone-accent-ink',
    },
  ] as const;

  return (
    <section id="features" className="py-4">
      <div className="container mx-auto px-4 md:px-6">
        <div className="relative overflow-hidden rounded-3xl border border-tone-glass/60 bg-tone-glass/55 p-6 shadow-sm backdrop-blur-xl md:p-10">
          {/* Iridescent header glow */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 top-0 h-px"
            style={{
              background:
                `linear-gradient(90deg, transparent 0%, ${brandRgba('accent', 0.5)} 25%, ${brandRgba('accentMid', 0.7)} 50%, ${brandRgba('accentDeep', 0.5)} 75%, transparent 100%)`,
            }}
          />
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[0.78fr_1.45fr] lg:gap-14">
            {/* Left: title + checklist */}
            <div>
              <h2 className="text-4xl font-extrabold leading-[1.1] tracking-tight text-tone-ink-strong md:text-[42px]">
                {t('landing:features.titleLine1')}
                <br />
                <span className="bg-gradient-to-r from-tone-accent-gradient-start to-tone-accent-gradient-end bg-clip-text text-transparent">
                  {t('landing:features.titleHighlighted')}
                </span>
              </h2>
              <p className="mt-5 max-w-[270px] text-[13px] leading-relaxed text-tone-ink-muted">
                {t('landing:features.body')}
              </p>
              <ul className="mt-6 space-y-3 text-[13px] text-tone-ink-body">
                {[
                  'landing:features.bullet1',
                  'landing:features.bullet2',
                  'landing:features.bullet3',
                  'landing:features.bullet4',
                ].map((key) => (
                  <li key={key} className="flex items-center gap-2.5">
                    <span className="grid h-[18px] w-[18px] flex-shrink-0 place-items-center rounded-full border border-tone-accent-line/70 bg-tone-glass/70">
                      <svg className="h-2.5 w-2.5 text-tone-accent-icon" viewBox="0 0 12 12" fill="none">
                        <path
                          d="M2.5 6.5L5 9L10 3.5"
                          stroke="currentColor"
                          strokeWidth="2.2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                    {t(key as TranslationKey)}
                  </li>
                ))}
              </ul>
            </div>

            {/* Right: feature grid */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {features.map((f) => (
                <motion.div
                  key={f.titleKey}
                  whileHover={{ y: -3 }}
                  transition={{ type: 'spring', stiffness: 260, damping: 22 }}
                  className={`group relative flex items-start gap-4 rounded-2xl border border-tone-accent-hairline/70 bg-gradient-to-br from-tone-accent-soft/50 via-tone-glass/70 to-tone-glass/80 p-5 shadow-[0_2px_12px_-4px_rgba(var(--brand-rgb-accent-deep),calc(var(--shadow-strength)*0.08))] transition-all hover:border-tone-accent-soft-edge/80 hover:shadow-[0_14px_32px_-12px_rgba(var(--brand-rgb-accent-mid),calc(var(--shadow-strength)*0.2))]`}
                >
                  {/* Icon container — light pastel bg + colored icon */}
                  <div
                    className={cn(
                      'grid h-12 w-12 flex-shrink-0 place-items-center rounded-xl',
                      f.iconBg,
                      f.iconColor
                    )}
                  >
                    {f.icon}
                  </div>
                  {/* Text */}
                  <div className="min-w-0 flex-1 pt-0.5">
                    <h3 className="text-[15px] font-bold leading-snug text-tone-ink-strong">
                      {t(f.titleKey)}
                    </h3>
                    <p className="mt-1 text-[13px] leading-relaxed text-tone-ink-muted">
                      {t(f.descKey)}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* COACHING JOURNEY — 5-step flow (reference layout)                   */
/* ------------------------------------------------------------------ */
/** Frosted-glass surfaces — coaching journey blocks */
function JourneyMirrorSheen({ rounded = '2xl' }: { rounded?: 'xl' | '2xl' | '3xl' }) {
  const roundClass =
    rounded === '3xl' ? 'rounded-t-3xl' : rounded === 'xl' ? 'rounded-t-xl' : 'rounded-t-2xl';

  return (
    <>
      <div
        aria-hidden
        className={cn(
          'pointer-events-none absolute inset-x-0 top-0 z-[1] h-[40%] bg-gradient-to-b from-tone-glass/65 via-tone-glass/20 to-transparent',
          roundClass
        )}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-4 bottom-0 z-[1] h-px bg-gradient-to-r from-transparent via-tone-glass/75 to-transparent"
      />
    </>
  );
}

function JourneyStepIconRing({ icon: Icon }: { icon: (typeof JOURNEY_STEPS)[number]['icon'] }) {
  return (
    <div className="relative shrink-0">
      <div
        aria-hidden
        className={`absolute -inset-4 rounded-full bg-[radial-gradient(circle,rgba(var(--brand-rgb-accent-soft),0.5)_0%,rgba(var(--brand-rgb-accent-light),0.15)_45%,transparent_72%)]`}
      />
      <div
        className={cn(
          'relative grid h-[76px] w-[76px] place-items-center overflow-hidden rounded-full',
          JOURNEY_GLASS_SOFT
        )}
      >
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-2 top-1.5 h-[45%] rounded-full bg-gradient-to-b from-tone-glass/60 via-tone-glass/15 to-transparent"
        />
        <div
          className={`relative grid h-14 w-14 place-items-center rounded-full shadow-[0_8px_24px_-6px_rgba(var(--brand-rgb-accent),calc(var(--shadow-strength)*0.45)),inset_0_1px_0_rgba(var(--brand-rgb-surface-card),calc(var(--shadow-strength)*0.4))]`}
          style={{
            background: `linear-gradient(145deg, ${brandColors.accent} 0%, ${brandColors.accentSoft} 48%, ${brandColors.accentHighlight} 100%)`,
          }}
        >
          <Icon className="h-7 w-7 text-tone-on-accent" strokeWidth={1.75} />
        </div>
        <span className={`absolute bottom-1 right-1 grid h-6 w-6 place-items-center rounded-full border-2 border-tone-surface bg-gradient-to-br from-[var(--brand-accent)] to-[var(--brand-accent-light)] shadow-[0_4px_12px_-2px_rgba(var(--brand-rgb-accent),calc(var(--shadow-strength)*0.55))]`}>
          <Sparkles className="h-3 w-3 text-tone-on-accent" strokeWidth={2.5} />
        </span>
      </div>
    </div>
  );
}

function JourneyStepLabel({
  titleKey,
  descriptionKey,
  icon,
}: {
  titleKey: TranslationKey;
  descriptionKey: TranslationKey;
  icon: (typeof JOURNEY_STEPS)[number]['icon'];
}) {
  const { t } = useTranslation();
  const title = t(titleKey);
  const description = t(descriptionKey);
  const titleLines = title.includes(' ')
    ? [title.split(' ')[0], title.split(' ').slice(1).join(' ')]
    : [title];

  return (
    <div className="flex w-full min-w-0 flex-col items-center px-1 text-center">
      <JourneyStepIconRing icon={icon} />

      <div className="mt-5 max-w-[220px]">
        <h4 className="text-[18px] font-extrabold leading-[1.2] tracking-tight text-tone-ink-strong md:text-[19px]">
          {titleLines.map((line, i) => (
            <span key={line} className={cn(i > 0 && 'block')}>
              {line}
            </span>
          ))}
        </h4>
        <p className="mt-3 text-[13px] leading-[1.55] text-tone-ink-muted">{description}</p>
      </div>
    </div>
  );
}

function JourneyStepCard({
  children,
  variant = 'solid',
}: {
  children: ReactNode;
  variant?: 'solid' | 'glass' | 'bare';
}) {
  if (variant === 'glass' || variant === 'bare') {
    return <div className="relative min-w-0 w-full">{children}</div>;
  }

  return (
    <div className={`rounded-[20px] border border-tone-card-edge/90 bg-tone-surface p-5 shadow-[0_8px_30px_-12px_rgba(var(--brand-rgb-accent),calc(var(--shadow-strength)*0.14))] md:p-6`}>
      {children}
    </div>
  );
}

function JourneyStepSlide({
  titleKey,
  descriptionKey,
  icon,
  children,
}: {
  titleKey: TranslationKey;
  descriptionKey: TranslationKey;
  icon: (typeof JOURNEY_STEPS)[number]['icon'];
  children: ReactNode;
}) {
  return (
    <div className="flex w-full min-w-0 flex-col items-center gap-6 lg:flex-row lg:items-center lg:gap-10">
      <div className="flex w-full shrink-0 items-center justify-center lg:w-[min(240px,280px)] lg:max-w-[280px]">
        <JourneyStepLabel titleKey={titleKey} descriptionKey={descriptionKey} icon={icon} />
      </div>
      <div className="flex min-w-0 w-full flex-1 items-center self-stretch">
        <JourneyStepCard variant="bare">{children}</JourneyStepCard>
      </div>
    </div>
  );
}

function CoachingJourneyCarousel({
  stepPanels,
  autoPlay = true,
}: {
  stepPanels: readonly ReactNode[];
  autoPlay?: boolean;
}) {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion();
  const maxIndex = JOURNEY_STEP_COUNT - 1;
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const goNext = useCallback(() => {
    setIndex((i) => (i >= maxIndex ? 0 : i + 1));
  }, [maxIndex]);

  const goPrev = useCallback(() => {
    setIndex((i) => (i <= 0 ? maxIndex : i - 1));
  }, [maxIndex]);

  useEffect(() => {
    if (!autoPlay || reduceMotion || paused) {
      return;
    }

    const timer = window.setInterval(() => {
      setIndex((i) => (i >= maxIndex ? 0 : i + 1));
    }, JOURNEY_CAROUSEL_INTERVAL_MS);

    return () => window.clearInterval(timer);
  }, [autoPlay, reduceMotion, paused, maxIndex]);

  return (
    <div className="relative w-full">
      <div className="relative rounded-3xl border border-tone-glass/60 bg-tone-glass/55 p-6 shadow-sm backdrop-blur-xl md:p-8">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-px"
          style={{
            background:
              `linear-gradient(90deg, transparent 0%, ${brandRgba('accent', 0.5)} 25%, ${brandRgba('accentMid', 0.7)} 50%, ${brandRgba('accentDeep', 0.5)} 75%, transparent 100%)`,
          }}
        />
        <JourneyMirrorSheen rounded="3xl" />

        <div
          className="relative z-[2] min-h-[min(72vh,700px)] min-h-[420px]"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onFocusCapture={() => setPaused(true)}
          onBlurCapture={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
              setPaused(false);
            }
          }}
        >
          <button
            type="button"
            aria-label={t('landing:a11y.previousStep')}
            onClick={goPrev}
            className={`absolute -left-3 top-1/2 z-10 -translate-y-1/2 rounded-full border border-tone-glass/70 bg-tone-glass/80 p-2 shadow-md backdrop-blur-md transition-all hover:bg-tone-surface hover:shadow-[0_8px_20px_-6px_rgba(var(--brand-rgb-accent-deep),calc(var(--shadow-strength)*0.35))]`}
          >
            <ChevronLeft className="h-4 w-4 text-tone-ink-body" />
          </button>
          <button
            type="button"
            aria-label={t('landing:a11y.nextStep')}
            onClick={goNext}
            className={`absolute -right-3 top-1/2 z-10 -translate-y-1/2 rounded-full border border-tone-glass/70 bg-tone-glass/80 p-2 shadow-md backdrop-blur-md transition-all hover:bg-tone-surface hover:shadow-[0_8px_20px_-6px_rgba(var(--brand-rgb-accent-deep),calc(var(--shadow-strength)*0.35))]`}
          >
            <ChevronRight className="h-4 w-4 text-tone-ink-body" />
          </button>

          <div className="h-full overflow-hidden">
            <motion.div
              className="flex h-full"
              animate={{ x: `-${index * 100}%` }}
              transition={reduceMotion ? { duration: 0 } : JOURNEY_CAROUSEL_TRANSITION}
            >
              {JOURNEY_STEPS.map((item, stepIndex) => (
                <div
                  key={item.step}
                  className="flex w-full shrink-0 items-center px-1 py-2 sm:px-2 sm:py-4"
                >
                  <JourneyStepSlide
                    titleKey={item.titleKey}
                    descriptionKey={item.descriptionKey}
                    icon={item.icon}
                  >
                    {stepPanels[stepIndex]}
                  </JourneyStepSlide>
                </div>
              ))}
            </motion.div>
          </div>

          <div className="mt-4 flex items-center justify-center gap-1.5">
            {JOURNEY_STEPS.map((item, i) => (
              <button
                key={item.step}
                type="button"
                aria-label={`Go to step ${item.step}: ${t(item.titleKey)}`}
                aria-current={i === index ? 'true' : undefined}
                onClick={() => setIndex(i)}
                className={cn(
                  'h-1.5 rounded-full transition-all',
                  i === index
                    ? `w-5 bg-gradient-to-r from-[var(--brand-accent)] via-[var(--brand-accent-mid)] to-[var(--brand-accent-deep)]`
                    : 'w-1.5 bg-tone-indicator-idle/80 hover:bg-tone-accent-line'
                )}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function ProgressSummaryStatCard({
  value,
  labelKey,
  icon: Icon,
  iconWrap,
  iconColor,
  valueColor,
}: (typeof PROGRESS_SUMMARY_STATS)[number]) {
  const { t } = useTranslation();
  return (
    <div
      className={cn(
        'relative flex flex-col items-center overflow-hidden rounded-2xl px-4 py-5 text-center sm:px-5 sm:py-6',
        JOURNEY_GLASS_SOFT
      )}
    >
      <JourneyMirrorSheen />
      <div className="relative z-[2]">
        <div
          className={cn(
            `grid h-12 w-12 place-items-center rounded-xl shadow-[inset_0_1px_0_rgba(var(--brand-rgb-surface-card),calc(var(--shadow-strength)*0.9))]`,
            iconWrap
          )}
        >
          <Icon className={cn('h-5 w-5', iconColor)} strokeWidth={2.2} />
        </div>
        <p
          className={cn('mt-4 text-[26px] font-extrabold leading-none tracking-tight', valueColor)}
        >
          {value}
        </p>
        <p className="mt-2 text-[12px] font-medium text-tone-ink-muted">{t(labelKey)}</p>
      </div>
    </div>
  );
}

function ProgressMedalIcon() {
  return (
    <svg width="56" height="56" viewBox="0 0 56 56" fill="none" aria-hidden className="shrink-0">
      <circle cx="28" cy="30" r="18" fill={brandColors.rewardMid} />
      <circle cx="28" cy="30" r="14" fill={brandColors.rewardBase} />
      <path d="M18 22 L14 10 L22 16 L28 8 L34 16 L42 10 L38 22 Z" fill={brandColors.accent} />
      <path
        d="M22 30 L26 34 L34 26"
        stroke="white"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <ellipse cx="28" cy="44" rx="10" ry="3" fill={brandColors.rewardBase} opacity="0.35" />
    </svg>
  );
}

function JourneyPersonaTag({ children }: { children: ReactNode }) {
  return (
    <span className={`inline-flex items-center rounded-full border border-tone-glass/80 bg-[color-mix(in_srgb,var(--brand-canvas-alt)_80%,transparent)] px-3 py-1 text-[11px] font-semibold text-tone-ink-secondary backdrop-blur-sm`}>
      {children}
    </span>
  );
}

function JourneyPersonaPickCard({
  persona,
}: {
  persona: (typeof JOURNEY_PERSONA_OPTIONS)[number];
}) {
  const { t } = useTranslation();
  const isActive = persona.selected;
  const rotateY = isActive ? 0 : -14;
  const translateZ = isActive ? 24 : 0;

  return (
    <motion.div
      className={cn(
        'relative flex h-96 w-[min(100%,280px)] min-w-[240px] max-w-[280px] shrink-0 flex-col rounded-2xl',
        isActive ? 'z-[3]' : 'z-[1]'
      )}
      style={{
        transformStyle: 'preserve-3d',
        transformOrigin: isActive ? 'center center' : 'left center',
      }}
      initial={false}
      animate={{
        rotateY,
        scale: isActive ? 1 : 0.9,
        opacity: isActive ? 1 : 0.82,
        z: translateZ,
      }}
      transition={{ duration: 0.5, ease: [0.22, 0.68, 0.24, 1] }}
      whileHover={
        isActive
          ? { y: -6, rotateY: -1, scale: 1.01, z: 32 }
          : { rotateY: -10, scale: 0.92, opacity: 0.9 }
      }
    >
      {/* 3D floor shadow */}
      <div
        aria-hidden
        className={cn(
          'pointer-events-none absolute -bottom-4 left-1/2 h-10 w-[88%] -translate-x-1/2 rounded-[100%] blur-xl transition-opacity',
          isActive ? `bg-[color-mix(in_srgb,var(--brand-accent)_25%,transparent)] opacity-100` : 'bg-tone-ground-shadow/15 opacity-60'
        )}
        style={{ transform: 'translateX(-50%) rotateX(72deg) scaleY(0.35)' }}
      />

      <div
        className={cn(
          'relative flex h-full w-full flex-col items-center overflow-hidden rounded-2xl px-6 pb-7 pt-6',
          isActive
            ? cn(
                JOURNEY_GLASS_SOFT,
                `shadow-[0_32px_64px_-24px_rgba(var(--brand-rgb-accent),calc(var(--shadow-strength)*0.45)),0_16px_40px_calc(-16px)_rgba(var(--brand-rgb-accent-mid),calc(var(--shadow-strength)*0.3)),inset_0_1px_0_rgba(var(--brand-rgb-surface-card),calc(var(--shadow-strength)*0.98)),inset_0_calc(-20px)_40px_calc(-20px)_rgba(var(--brand-rgb-surface-card),calc(var(--shadow-strength)*0.35))]`
              )
            : cn(JOURNEY_GLASS_PANEL, 'opacity-90 saturate-[0.9]')
        )}
        style={{ transform: 'translateZ(0)' }}
      >
        <JourneyMirrorSheen />
        {isActive ? (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 rounded-2xl p-[1.5px]"
            style={{
              background:
                `linear-gradient(145deg, ${brandColors.accent} 0%, ${brandColors.accentMid} 40%, ${brandColors.accentDeep} 70%, ${brandColors.accentLight} 100%)`,
              WebkitMask: `linear-gradient(${brandColors.maskOpaque} 0 0) content-box, linear-gradient(${brandColors.maskOpaque} 0 0)`,
              WebkitMaskComposite: 'xor',
              maskComposite: 'exclude',
            }}
          />
        ) : null}

        <div
          className={cn(
            'absolute right-4 top-4 z-10 grid h-8 w-8 place-items-center rounded-full',
            isActive
              ? `bg-gradient-to-br from-[var(--brand-accent)] to-[var(--brand-accent-soft)] shadow-[0_6px_16px_-2px_rgba(var(--brand-rgb-accent),calc(var(--shadow-strength)*0.55))]`
              : 'border border-tone-track/90 bg-tone-glass/60'
          )}
          style={{ transform: 'translateZ(12px)' }}
        >
          <Check
            className={cn('h-4 w-4', isActive ? 'text-tone-on-accent' : 'text-tone-ink-disabled')}
            strokeWidth={3}
          />
        </div>

        <div
          className={`relative mt-2 h-[104px] w-[104px] shrink-0 overflow-hidden rounded-full border-[3px] border-tone-glass/95 shadow-[0_12px_32px_-8px_rgba(var(--brand-rgb-accent),calc(var(--shadow-strength)*0.45))]`}
          style={{ transform: 'translateZ(20px)' }}
        >
          <img
            src={persona.photo}
            alt={t(persona.nameKey)}
            className={cn('h-full w-full object-cover', !isActive && 'opacity-90 grayscale-[0.2]')}
          />
        </div>

        <div className="mt-5 flex flex-1 flex-col items-center justify-between">
          <div className="text-center">
            <h4
              className={cn(
                'text-[16px] font-bold',
                isActive ? 'text-tone-ink-strong' : 'text-tone-ink-secondary'
              )}
            >
              {t(persona.nameKey)}
            </h4>
            <p className="mt-1.5 text-[12px] text-tone-ink-muted">
              {t('landing:persona.ageLabel')} {persona.age} • {t(persona.genderKey)}
            </p>
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              {persona.tags.map((tag) => (
                <JourneyPersonaTag key={tag}>{t(tag)}</JourneyPersonaTag>
              ))}
            </div>
          </div>

          <p className="mt-5 px-1 text-center text-[12px] leading-relaxed text-tone-ink-muted">
            {t(persona.description)}
          </p>
        </div>
      </div>
    </motion.div>
  );
}

function StepChoosePersonaCard() {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-8 lg:flex-row lg:items-center lg:gap-10 xl:gap-14">
      <div className="shrink-0 lg:w-[min(300px,38%)]">
        <h3 className="text-xl font-extrabold tracking-tight text-tone-ink-strong md:text-2xl">
          {t('landing:journey.choosePersona.title')}
        </h3>
        <p className="mt-2 max-w-[280px] text-[13px] leading-relaxed text-tone-ink-muted md:text-sm">
          {t('landing:journey.choosePersona.subtitle')}
        </p>
        <GradientButton className="mt-6 gap-2 px-6 py-3 text-[13px]">
          <Plus className="h-4 w-4" strokeWidth={2.5} />
          {t('landing:journey.choosePersona.action')}
        </GradientButton>
      </div>

      <div className="relative min-w-0 flex-1">
        <div
          className="relative flex min-h-[26rem] items-center justify-start gap-5 overflow-visible px-2 py-6 sm:gap-8 sm:pl-2"
          style={{ perspective: '1400px' }}
        >
          {brandJourneyOptions().map((persona) => (
            <JourneyPersonaPickCard key={persona.nameKey} persona={persona} />
          ))}
        </div>

        <div className="mt-6 flex items-center justify-end gap-2 pr-1">
          {Array.from({ length: JOURNEY_PERSONA_PAGE_COUNT }, (_, i) => (
            <span
              key={i}
              className={cn(
                'rounded-full transition-all',
                i === 0
                  ? `h-2 w-2 bg-[var(--brand-accent)] shadow-[0_0_8px_rgba(var(--brand-rgb-accent),calc(var(--shadow-strength)*0.5))]`
                  : 'h-1.5 w-1.5 bg-tone-indicator-idle/80'
              )}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function ScenarioFormField({
  label,
  value,
  multiline = false,
}: {
  label: TranslationKey;
  value: string;
  multiline?: boolean;
}) {
  const { t } = useTranslation();

  const fieldClass =
    `mt-1.5 w-full resize-none rounded-lg border border-tone-accent-soft-edge/90 bg-tone-glass/80 px-3.5 py-2.5 text-[13px] text-tone-ink-emphasis shadow-[inset_0_1px_0_rgba(var(--brand-rgb-surface-card),calc(var(--shadow-strength)*0.85)),0_2px_8px_calc(-4px)_rgba(var(--brand-rgb-accent),calc(var(--shadow-strength)*0.06))] backdrop-blur-md outline-none`;

  return (
    <div>
      <label className="text-[12px] font-semibold text-tone-ink-muted">{t(label)}</label>
      {multiline ? (
        <textarea readOnly className={fieldClass} value={value} />
      ) : (
        <input readOnly className={fieldClass} value={value} />
      )}
    </div>
  );
}

function StepBuildScenarioCard() {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-6 md:gap-8">
      <div>
        <h3 className="text-xl font-extrabold tracking-tight text-tone-ink-strong md:text-[22px]">
          {t('landing:scenario.title')}
        </h3>
        <p className="mt-1.5 text-[13px] text-tone-ink-muted md:text-sm">
          {t('landing:scenario.subtitle')}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(280px,340px)] lg:gap-8">
        <div className={cn('rounded-2xl p-5 md:p-6', JOURNEY_GLASS_PANEL)}>
          <JourneyMirrorSheen />
          <div className="relative z-[2] space-y-5">
            <ScenarioFormField
              label="landing:scenario.field.title"
              value={t(SCENARIO_MOCK.title)}
            />
            <ScenarioFormField
              label="landing:scenario.field.situation"
              value={t(SCENARIO_MOCK.situation)}
              multiline
            />
            <ScenarioFormField
              label="landing:scenario.field.objective"
              value={t(SCENARIO_MOCK.objective)}
              multiline
            />
            <div className="border-t border-tone-glass/40 pt-5">
              <label className="text-[12px] font-semibold text-tone-ink-muted">
                {t('landing:scenario.field.difficulty')}
              </label>
              <div className="relative mt-1.5">
                <input
                  readOnly
                  type="text"
                  value={t(SCENARIO_MOCK.difficulty)}
                  className={`w-full rounded-lg border border-tone-glass/55 bg-tone-glass/45 px-3.5 py-2.5 pr-10 text-[13px] font-medium text-tone-ink-emphasis shadow-[inset_0_1px_0_rgba(var(--brand-rgb-surface-card),calc(var(--shadow-strength)*0.85))] backdrop-blur-md`}
                />
                <ChevronDown
                  className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-tone-ink-faint"
                  strokeWidth={2.2}
                />
              </div>
            </div>
          </div>
        </div>

        <div className={cn('rounded-2xl p-5 md:p-6', JOURNEY_GLASS_PANEL)}>
          <JourneyMirrorSheen />
          <p className="relative z-[2] text-[14px] font-bold text-tone-ink-strong">
            {t('landing:scenario.preview.title')}
          </p>

          <div className={`relative z-[2] mt-4 overflow-hidden rounded-xl border border-tone-glass/50 bg-[color-mix(in_srgb,var(--brand-canvas-alt)_45%,transparent)] p-4 shadow-[0_8px_24px_-10px_rgba(var(--brand-rgb-accent),calc(var(--shadow-strength)*0.15)),inset_0_1px_0_rgba(var(--brand-rgb-surface-card),calc(var(--shadow-strength)*0.85))] backdrop-blur-xl`}>
            <JourneyMirrorSheen rounded="xl" />
            <div className="flex gap-3">
              <div className={`grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-gradient-to-br from-[var(--brand-accent)] to-[var(--brand-accent-mid)] shadow-[0_4px_12px_-4px_rgba(var(--brand-rgb-accent),calc(var(--shadow-strength)*0.45))]`}>
                <FileText className="h-4 w-4 text-tone-on-accent" strokeWidth={2.2} />
              </div>
              <div className="min-w-0">
                <p className={`text-[14px] font-bold text-[var(--brand-accent-text)]`}>{t(SCENARIO_MOCK.title)}</p>
                <p className="mt-2 text-[12px] leading-relaxed text-tone-ink-secondary">
                  {t(SCENARIO_MOCK.situation)}
                </p>
              </div>
            </div>
          </div>

          <div className="relative z-[2] mt-6">
            <p className="text-[14px] font-bold text-tone-ink-strong">
              {t('landing:scenario.preview.talkingPoints.title')}
            </p>
            <ul className="mt-4 space-y-3">
              {SCENARIO_MOCK.talkingPoints.map((point) => (
                <li key={point} className="flex items-start gap-3">
                  <span className={`mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-gradient-to-br from-[var(--brand-accent)] to-[var(--brand-accent-soft)] shadow-[0_2px_8px_-2px_rgba(var(--brand-rgb-accent),calc(var(--shadow-strength)*0.4))]`}>
                    <Check className="h-3 w-3 text-tone-on-accent" strokeWidth={3} />
                  </span>
                  <span className="text-[13px] leading-snug text-tone-ink-secondary">{t(point)}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

function JourneyGlassPanel({
  title,
  children,
  className,
}: {
  title: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('rounded-2xl p-4 md:p-5', JOURNEY_GLASS_PANEL, className)}>
      <JourneyMirrorSheen />
      <p className="relative z-[2] text-[14px] font-bold text-tone-ink-strong">{title}</p>
      <div className="relative z-[2] mt-4">{children}</div>
    </div>
  );
}

function JourneyChatAvatar({
  src,
  fallbackSrc,
  alt,
  size = 'md',
}: {
  src: string;
  fallbackSrc: string;
  alt: string;
  size?: 'sm' | 'md';
}) {
  const [imgSrc, setImgSrc] = useState(src);
  const box = size === 'sm' ? 'h-8 w-8' : 'h-10 w-10';
  const text = size === 'sm' ? 'text-[10px]' : 'text-[11px]';

  const initials = getInitials(alt);

  return (
    <div
      className={cn(
        box,
        `relative shrink-0 overflow-hidden rounded-full border-2 border-tone-glass/90 bg-gradient-to-br from-[var(--brand-surface-tint)] to-[var(--brand-surface-tint-alt)] shadow-sm`
      )}
    >
      {imgSrc ? (
        <img
          src={imgSrc}
          alt={alt}
          className="block h-full w-full object-cover object-center"
          loading="lazy"
          decoding="async"
          onError={() => {
            if (imgSrc !== fallbackSrc) {
              setImgSrc(fallbackSrc);
              return;
            }
            setImgSrc('');
          }}
        />
      ) : (
        <span
          className={cn(`grid h-full w-full place-items-center font-bold text-[var(--brand-accent-deep)]`, text)}
        >
          {initials || '?'}
        </span>
      )}
    </div>
  );
}

function StepTalkToAiCard() {
  const { t } = useTranslation();
  const progressRing = 2 * Math.PI * 26;
  const progressOffset = progressRing * (1 - ROLEPLAY_SESSION.progress / 100);

  return (
    <div className="relative">
      <div
        aria-hidden
        className="pointer-events-none absolute -left-8 top-1/4 h-36 w-36 rounded-full blur-3xl"
        style={{
          background: `radial-gradient(circle, ${brandRgba('glowWarm', 0.35)} 0%, transparent 70%)`,
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -right-4 bottom-0 h-40 w-40 rounded-full blur-3xl"
        style={{
          background: `radial-gradient(circle, ${brandRgba('accentSoft', 0.4)} 0%, transparent 70%)`,
        }}
      />

      <div className="relative grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(240px,280px)] lg:gap-6">
        {/* Chat panel */}
        <div className={cn('relative overflow-hidden rounded-2xl', JOURNEY_GLASS_PANEL)}>
          <JourneyMirrorSheen />
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-tone-accent-line/60 to-transparent"
          />

          <div className="relative z-[2] flex items-center justify-between border-b border-tone-glass/50 px-4 py-3.5 md:px-5">
            <div className="flex min-w-0 items-center gap-3">
              <JourneyChatAvatar
                src={brandPhoto(brand.landingJourneyPersonas, 'landing:persona.sophia.name', '/persona-sophia.jpg')}
                fallbackSrc={ROLEPLAY_SESSION.personaPhoto}
                alt={ROLEPLAY_SESSION.personaName}
                size="md"
              />
              <div>
                <p className="text-[11px] font-medium text-tone-ink-muted">
                  {t('landing:roleplay.chat.roleplayWith')}
                </p>
                <div className="flex items-center gap-2">
                  <p className={`text-[14px] font-bold text-[var(--brand-accent-text)]`}>
                    {brand.landingJourneyPersonas ? t('landing:persona.sophia.name') : ROLEPLAY_SESSION.personaName}
                  </p>
                  <span className="inline-flex items-center gap-1 rounded-full bg-tone-positive-surface px-2 py-0.5 text-[10px] font-bold text-tone-positive">
                    <span className="h-1.5 w-1.5 rounded-full bg-tone-positive-solid" />
                    {t('landing:roleplay.chat.live')}
                  </span>
                </div>
              </div>
            </div>
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-lg border border-tone-critical-subtle/90 bg-tone-glass/80 px-3 py-1.5 text-[12px] font-semibold text-tone-critical shadow-sm backdrop-blur-sm transition-colors hover:bg-tone-critical-surface/80"
            >
              <PhoneOff className="h-3.5 w-3.5" strokeWidth={2.2} />
              {t('landing:roleplay.chat.endSession')}
            </button>
          </div>

          <div className="relative z-[2] space-y-4 px-4 py-4 md:px-5 md:py-5">
            {ROLEPLAY_SESSION.messages.map((msg, idx) => {
              const isCoach = msg.role === 'coach';
              return (
                <div
                  key={idx}
                  className={cn(
                    'flex items-end gap-2.5',
                    isCoach ? 'flex-row-reverse' : 'flex-row'
                  )}
                >
                  <JourneyChatAvatar
                    src={
                      isCoach
                        ? brandPhoto(brand.landingPersonas, 'landing:personas.emma.name', '/persona-emma.jpg')
                        : brandPhoto(brand.landingJourneyPersonas, 'landing:persona.sophia.name', '/persona-sophia.jpg')
                    }
                    fallbackSrc={
                      isCoach ? ROLEPLAY_SESSION.coachPhoto : ROLEPLAY_SESSION.personaPhoto
                    }
                    alt={isCoach ? 'You' : ROLEPLAY_SESSION.personaName}
                    size="sm"
                  />
                  <div
                    className={cn(
                      'flex min-w-0 max-w-[82%] flex-col',
                      isCoach ? 'items-end' : 'items-start'
                    )}
                  >
                    <div
                      className={cn(
                        `rounded-2xl px-3.5 py-2.5 text-[13px] leading-relaxed backdrop-blur-xl shadow-[0_4px_16px_-8px_rgba(var(--brand-rgb-accent),calc(var(--shadow-strength)*0.12)),inset_0_1px_0_rgba(var(--brand-rgb-surface-card),calc(var(--shadow-strength)*0.9))]`,
                        isCoach
                          ? `rounded-br-md border border-tone-glass/55 bg-gradient-to-br from-[color-mix(in_srgb,var(--brand-accent-tint)_75%,transparent)] via-[color-mix(in_srgb,var(--brand-surface-tint)_65%,transparent)] to-[color-mix(in_srgb,var(--brand-surface-tint-alt)_60%,transparent)] text-tone-ink-emphasis`
                          : `rounded-bl-md border border-tone-glass/55 bg-gradient-to-br from-[color-mix(in_srgb,var(--brand-surface-tint-alt)_55%,transparent)] via-[color-mix(in_srgb,var(--brand-canvas-alt)_60%,transparent)] to-[color-mix(in_srgb,var(--brand-surface-tint-alt)_55%,transparent)] text-tone-ink-body`
                      )}
                    >
                      {t(msg.text)}
                    </div>
                    <div
                      className={cn(
                        'mt-1 flex items-center gap-1.5 px-1',
                        isCoach ? 'justify-end' : 'justify-start'
                      )}
                    >
                      <span className="text-[10px] font-medium text-tone-ink-faint">{msg.time}</span>
                      {isCoach ? (
                        <CheckCheck className={`h-3.5 w-3.5 text-[var(--brand-accent)]`} strokeWidth={2.5} />
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="relative z-[2] border-t border-tone-glass/50 px-4 py-3.5 md:px-5">
            <div
              className={cn('flex items-center gap-2 rounded-full px-4 py-2', JOURNEY_GLASS_SOFT)}
            >
              <span className="flex-1 text-[13px] text-tone-ink-faint">
                {t('landing:roleplay.chat.inputPlaceholder')}
              </span>
              <button
                type="button"
                aria-label={t('landing:a11y.sendMessage')}
                className={`grid h-9 w-9 shrink-0 place-items-center rounded-full bg-gradient-to-br from-[var(--brand-accent)] to-[var(--brand-accent-soft)] text-tone-on-accent shadow-[0_6px_16px_-4px_rgba(var(--brand-rgb-accent),calc(var(--shadow-strength)*0.55))]`}
              >
                <Send className="h-4 w-4" strokeWidth={2.2} />
              </button>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="flex flex-col gap-4">
          <JourneyGlassPanel title={t('landing:roleplay.tips.title')}>
            <ul className="space-y-3.5">
              {ROLEPLAY_SESSION.tips.map((tip) => {
                const Icon = tip.icon;
                return (
                  <li key={tip.text} className="flex items-center gap-3">
                    <span className={`grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[var(--brand-canvas-alt)] shadow-[inset_0_1px_0_rgba(var(--brand-rgb-surface-card),calc(var(--shadow-strength)*0.9))]`}>
                      <Icon className={`h-4 w-4 text-[var(--brand-accent)]`} strokeWidth={2.2} />
                    </span>
                    <span className="text-[13px] font-medium text-tone-ink-secondary">{t(tip.text)}</span>
                  </li>
                );
              })}
            </ul>
          </JourneyGlassPanel>

          <JourneyGlassPanel title={t('landing:roleplay.progress.title')}>
            <div className="flex items-center gap-4">
              <div className="relative h-[72px] w-[72px] shrink-0">
                <svg className="h-[72px] w-[72px] -rotate-90" viewBox="0 0 64 64">
                  <defs>
                    <linearGradient id="roleplayProgressGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor={brandColors.accent} />
                      <stop offset="100%" stopColor={brandColors.accentMid} />
                    </linearGradient>
                  </defs>
                  <circle cx="32" cy="32" r="26" stroke={brandColors.elevatedAlt} strokeWidth="6" fill="none" />
                  <circle
                    cx="32"
                    cy="32"
                    r="26"
                    stroke="url(#roleplayProgressGrad)"
                    strokeWidth="6"
                    fill="none"
                    strokeDasharray={progressRing}
                    strokeDashoffset={progressOffset}
                    strokeLinecap="round"
                  />
                </svg>
                <span className="absolute inset-0 flex items-center justify-center text-[15px] font-extrabold text-tone-ink-strong">
                  {ROLEPLAY_SESSION.progress}%
                </span>
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[13px] font-semibold text-tone-ink-body">
                  {t('landing:roleplay.progress.message')}
                </p>
                <div className="mt-3 h-2 overflow-hidden rounded-full bg-tone-track/80">
                  <div
                    className={`h-full rounded-full bg-gradient-to-r from-[var(--brand-accent)] via-[var(--brand-accent-soft)] to-[var(--brand-accent-mid)] shadow-[0_0_12px_rgba(var(--brand-rgb-accent),calc(var(--shadow-strength)*0.35))]`}
                    style={{ width: `${ROLEPLAY_SESSION.progress}%` }}
                  />
                </div>
              </div>
            </div>
          </JourneyGlassPanel>
        </div>
      </div>
    </div>
  );
}

function StepGetInsightsCard() {
  const { t } = useTranslation();
  const progressRing = 2 * Math.PI * 42;
  const progressOffset = progressRing * (1 - AI_FEEDBACK_MOCK.score / 100);

  return (
    <div className="relative flex flex-col gap-6 md:gap-8">
      <div
        aria-hidden
        className="pointer-events-none absolute -left-6 top-0 h-32 w-32 rounded-full blur-3xl"
        style={{
          background: `radial-gradient(circle, ${brandRgba('accentHighlight', 0.35)} 0%, transparent 70%)`,
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -right-4 bottom-8 h-36 w-36 rounded-full blur-3xl"
        style={{
          background: `radial-gradient(circle, ${brandRgba('glowWarm', 0.3)} 0%, transparent 70%)`,
        }}
      />

      <h3 className="relative text-xl font-extrabold tracking-tight text-tone-ink-strong md:text-[22px]">
        {t('landing:aiFeedback.title')}
      </h3>

      {/* Score overview + skill breakdown */}
      <div className={cn('relative rounded-2xl p-5 md:p-6 lg:p-7', JOURNEY_GLASS_PANEL)}>
        <JourneyMirrorSheen />
        <div className="relative z-[2] grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-10">
          <div>
            <p className="text-[13px] font-bold text-tone-ink-body">
              {t('landing:aiFeedback.scoreOverview')}
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-5 sm:gap-6">
              <div className="relative h-[120px] w-[120px] shrink-0">
                <svg className="h-[120px] w-[120px] -rotate-90" viewBox="0 0 100 100">
                  <defs>
                    <linearGradient id="feedbackScoreGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor={brandColors.successVivid} />
                      <stop offset="100%" stopColor={brandColors.successVivid} />
                    </linearGradient>
                  </defs>
                  <circle cx="50" cy="50" r="42" stroke={brandColors.elevatedAlt} strokeWidth="8" fill="none" />
                  <circle
                    cx="50"
                    cy="50"
                    r="42"
                    stroke="url(#feedbackScoreGrad)"
                    strokeWidth="8"
                    fill="none"
                    strokeDasharray={progressRing}
                    strokeDashoffset={progressOffset}
                    strokeLinecap="round"
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-[32px] font-extrabold leading-none text-tone-ink-strong">
                    {AI_FEEDBACK_MOCK.score}
                  </span>
                  <span className="mt-0.5 text-[13px] font-semibold text-tone-ink-faint">/100</span>
                </div>
              </div>
              <div className="min-w-0">
                <p className="text-[17px] font-bold text-tone-positive">
                  {t('landing:aiFeedback.highlight')}
                </p>
                <p className="mt-2 max-w-[240px] text-[13px] leading-relaxed text-tone-ink-secondary">
                  {t('landing:aiFeedback.subtitle')}
                </p>
              </div>
            </div>
          </div>

          <div>
            <p className="text-[13px] font-bold text-tone-ink-body">
              {t('landing:aiFeedback.skillBreakdown')}
            </p>
            <ul className="mt-4 space-y-3.5">
              {AI_FEEDBACK_MOCK.skills.map((skill) => (
                <li key={skill.labelKey}>
                  <div className="mb-1.5 flex items-center justify-between gap-2">
                    <span className="text-[12px] font-medium text-tone-ink-secondary">
                      {t(skill.labelKey)}
                    </span>
                    <span className="text-[12px] font-bold text-tone-ink-emphasis">{skill.value}</span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-tone-track/70">
                    <div
                      className={`h-full rounded-full shadow-[0_0_10px_rgba(var(--brand-rgb-success-vivid),calc(var(--shadow-strength)*0.25))]`}
                      style={{
                        width: `${skill.value}%`,
                        backgroundColor: skill.color,
                      }}
                    />
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Feedback columns */}
      <div className="relative grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5">
        <div className={cn('rounded-2xl p-5 md:p-6', JOURNEY_GLASS_PANEL)}>
          <JourneyMirrorSheen />
          <p className="relative z-[2] text-[15px] font-bold text-tone-positive">
            {t('landing:aiFeedback.strengthsTitle')}
          </p>
          <ul className="relative z-[2] mt-4 space-y-3">
            {AI_FEEDBACK_MOCK.strengths.map((item) => (
              <li key={item} className="flex items-start gap-3">
                <span className={`mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-[var(--brand-success-tint)]`}>
                  <Check className="h-3 w-3 text-tone-positive-mark" strokeWidth={3} />
                </span>
                <span className="text-[13px] leading-snug text-tone-ink-secondary">{t(item)}</span>
              </li>
            ))}
          </ul>
        </div>

        <div
          className={cn(
            'relative overflow-hidden rounded-2xl border border-tone-critical-subtle/35 bg-gradient-to-br from-tone-glass/30 via-tone-critical-surface/25 to-tone-glass/25 p-5 md:p-6',
            JOURNEY_GLASS_PANEL
          )}
        >
          <JourneyMirrorSheen />
          <div
            aria-hidden
            className="pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full bg-tone-critical-subtle/30 blur-2xl"
          />
          <p className="relative flex items-center gap-2 text-[15px] font-bold text-tone-critical">
            <Target className="h-4 w-4 shrink-0" strokeWidth={2.2} />
            {t('landing:aiFeedback.improvementsTitle')}
          </p>
          <ul className="relative mt-4 space-y-3">
            {AI_FEEDBACK_MOCK.improvements.map((item) => (
              <li key={item} className="flex items-start gap-3">
                <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-tone-critical-soft">
                  <Check className="h-3 w-3 text-tone-critical-solid" strokeWidth={3} />
                </span>
                <span className="text-[13px] leading-snug text-tone-ink-secondary">{t(item)}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function StepBecomeBetterCard() {
  const { t } = useTranslation();

  return (
    <div className="relative flex flex-col gap-6 md:gap-8">
      <div
        aria-hidden
        className="pointer-events-none absolute -left-4 top-0 h-28 w-28 rounded-full blur-3xl"
        style={{
          background: `radial-gradient(circle, ${brandRgba('accentHighlight', 0.35)} 0%, transparent 70%)`,
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute bottom-0 right-0 h-32 w-32 rounded-full blur-3xl"
        style={{
          background: `radial-gradient(circle, ${brandRgba('accentSoft', 0.35)} 0%, transparent 70%)`,
        }}
      />

      <div className="relative">
        <h3 className="text-xl font-extrabold tracking-tight text-tone-ink-strong md:text-[22px]">
          {t('landing:progress.title')}
        </h3>
        <p className="mt-1.5 text-[13px] text-tone-ink-muted md:text-sm">
          {t('landing:progress.subtitle')}
        </p>
      </div>

      <div className="relative grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
        {PROGRESS_SUMMARY_STATS.map((stat) => (
          <ProgressSummaryStatCard key={stat.labelKey} {...stat} />
        ))}
      </div>

      <div className="relative grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1.65fr)_minmax(280px,1fr)] lg:gap-5">
        <div className={cn('rounded-2xl p-5 md:p-6', JOURNEY_GLASS_PANEL)}>
          <JourneyMirrorSheen />
          <h4 className="relative z-[2] text-[15px] font-bold text-tone-ink-strong">
            {t('landing:progress.recentSessions.title')}
          </h4>
          <ul className="relative z-[2] mt-4 divide-y divide-tone-track/50">
            {PROGRESS_RECENT_SESSIONS.map((session) => (
              <li
                key={session.nameKey}
                className="flex flex-wrap items-center gap-x-4 gap-y-2 py-4 first:pt-0 last:pb-0"
              >
                <span className="min-w-[140px] flex-1 text-[14px] font-semibold text-tone-ink-emphasis">
                  {t(session.nameKey)}
                </span>
                <span className="text-[14px] font-bold text-tone-positive">{session.score}</span>
                <span className="text-[12px] text-tone-ink-faint">{session.date}</span>
                <button
                  type="button"
                  className={`rounded-lg border border-[color-mix(in_srgb,var(--brand-accent)_40%,transparent)] bg-tone-glass/70 px-4 py-1.5 text-[12px] font-semibold text-[var(--brand-accent)] shadow-sm backdrop-blur-sm transition-colors hover:bg-[var(--brand-canvas-alt)]`}
                >
                  {t('landing:progress.viewAction')}
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className={cn('rounded-2xl p-5 md:p-6', JOURNEY_GLASS_PANEL)}>
          <JourneyMirrorSheen />
          <h4 className="relative z-[2] text-[15px] font-bold text-tone-ink-strong">
            {t('landing:progress.nextMilestone.title')}
          </h4>
          <div className="relative z-[2] mt-5 flex flex-col items-center text-center sm:items-start sm:text-left">
            <div className="relative mb-4 grid h-24 w-24 place-items-center">
              <div aria-hidden className={`absolute inset-0 rounded-full bg-[color-mix(in_srgb,var(--brand-accent-tint)_60%,transparent)] blur-md`} />
              <div className={`relative rounded-full bg-gradient-to-br from-[var(--brand-surface-tint)] to-[var(--brand-accent-tint)] p-2 shadow-[0_8px_24px_-8px_rgba(var(--brand-rgb-accent),calc(var(--shadow-strength)*0.35))]`}>
                <ProgressMedalIcon />
              </div>
            </div>
            <p className="text-[17px] font-bold text-tone-ink-strong">
              {t('landing:progress.nextMilestone.goal')}
            </p>
            <p className="mt-1 text-[13px] text-tone-ink-muted">
              {t('landing:progress.nextMilestone.subtitle')}
            </p>
            <div className="mt-5 flex w-full items-center gap-3">
              <div className={`h-2.5 flex-1 overflow-hidden rounded-full bg-[color-mix(in_srgb,var(--brand-accent-tint)_80%,transparent)]`}>
                <div
                  className={`h-full rounded-full bg-gradient-to-r from-[var(--brand-accent)] to-[var(--brand-accent-soft)] shadow-[0_0_10px_rgba(var(--brand-rgb-accent),calc(var(--shadow-strength)*0.35))]`}
                  style={{ width: '60%' }}
                />
              </div>
              <span className="shrink-0 text-[13px] font-bold text-tone-ink-body">3/5</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CoachingJourneySection() {
  const reduceMotion = useReducedMotion();
  const stepPanels = [
    <StepChoosePersonaCard key="1" />,
    <StepBuildScenarioCard key="2" />,
    <StepTalkToAiCard key="3" />,
    <StepGetInsightsCard key="4" />,
    <StepBecomeBetterCard key="5" />,
  ] as const;

  return (
    <section id="how-it-works" className="py-4">
      <div className="container mx-auto px-4 md:px-6">
        <CoachingJourneyCarousel stepPanels={stepPanels} autoPlay={!reduceMotion} />
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* CTA SECTION                                                         */
/* ------------------------------------------------------------------ */
function CTASection({ onRegister }: { onRegister: () => void }) {
  const { t } = useTranslation();

  return (
    <section className="py-4">
      <div className="container mx-auto px-4 md:px-6">
        <div className={`relative overflow-hidden rounded-3xl border border-tone-glass/70 bg-tone-glass/40 p-8 shadow-[0_30px_80px_-20px_rgba(var(--brand-rgb-accent),calc(var(--shadow-strength)*0.4)),0_10px_40px_calc(-10px)_rgba(var(--brand-rgb-accent-deep),calc(var(--shadow-strength)*0.18))] backdrop-blur-2xl md:p-12`}>
          <CtaInsuranceBackdrop />
          {/* Iridescent header strip — signature AI element */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 top-0 z-[1] h-px"
            style={{
              background:
                `linear-gradient(90deg, transparent 0%, ${brandRgba('accent', 0.6)} 20%, ${brandRgba('accentMid', 0.8)} 45%, ${brandRgba('accentDeep', 0.7)} 70%, ${brandRgba('accentLight', 0.5)} 90%, transparent 100%)`,
            }}
          />
          {/* Decorative gradient orbs — AI iridescent */}
          <div
            aria-hidden
            className="pointer-events-none absolute -right-24 -top-24 z-[1] h-80 w-80 rounded-full blur-3xl"
            style={{
              background:
                `radial-gradient(closest-side, ${brandRgba('accentDeep', 0.45)}, ${brandRgba('accentMid', 0.25)} 50%, transparent 75%)`,
            }}
          />
          <div
            aria-hidden
            className="pointer-events-none absolute -left-16 -bottom-10 z-[1] h-72 w-72 rounded-full blur-3xl"
            style={{
              background:
                `radial-gradient(closest-side, ${brandRgba('accentLight', 0.45)}, ${brandRgba('accent', 0.25)} 50%, transparent 75%)`,
            }}
          />
          <div
            aria-hidden
            className="pointer-events-none absolute right-1/3 bottom-0 z-[1] h-56 w-56 rounded-full blur-3xl"
            style={{
              background: `radial-gradient(closest-side, ${brandRgba('glowWarm', 0.35)}, transparent 70%)`,
            }}
          />

          <div className="relative z-[2] grid grid-cols-1 items-center">
            <div className="max-w-xl lg:max-w-2xl">
              <div className={`mb-3 inline-flex items-center gap-2 rounded-full border border-tone-glass/70 bg-tone-glass/65 px-3 py-1 text-[10.5px] font-bold uppercase tracking-[0.12em] text-[var(--brand-accent-deep)] shadow-sm backdrop-blur-xl`}>
                <span className={`grid h-3.5 w-3.5 place-items-center rounded-full bg-gradient-to-br from-[var(--brand-accent)] via-[var(--brand-accent-mid)] to-[var(--brand-accent-deep)]`}>
                  <Sparkles className="h-2 w-2 text-tone-on-accent" />
                </span>
                {t('landing:cta.badge')}
              </div>
              <h2 className="text-3xl font-extrabold leading-tight tracking-tight text-tone-ink-strong md:text-4xl">
                {t('landing:cta.titleLine1')}
                {t('landing:cta.titleHighlighted') && (
                  <>
                    <br />
                    <span
                      className="bg-clip-text text-transparent"
                      style={{
                        backgroundImage:
                          `linear-gradient(95deg, ${brandColors.accent} 0%, ${brandColors.accentMid} 50%, ${brandColors.accentDeep} 100%)`,
                      }}
                    >
                      {t('landing:cta.titleHighlighted')}
                    </span>
                  </>
                )}
              </h2>
              <p className="mt-3 max-w-md text-sm text-tone-ink-body md:text-base">
                {t('landing:cta.subtitle')}
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                {environment.registrationEnabled && (
                  <GradientButton onClick={onRegister} className="px-6 py-3 text-sm">
                    {t('landing:cta.primaryButton')}
                    <ArrowRight className="ml-1.5 h-4 w-4" />
                  </GradientButton>
                )}
                <GlassButton className="px-6 py-3 text-sm text-tone-ink-body">
                  {t('landing:cta.secondaryButton')}
                </GlassButton>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function CtaInsuranceBackdrop() {
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 z-0 overflow-hidden"
      style={{
        WebkitMaskImage: `radial-gradient(ellipse 108% 96% at 52% 48%, ${brandColors.maskOpaque} 52%, transparent 100%)`,
        maskImage: `radial-gradient(ellipse 108% 96% at 52% 48%, ${brandColors.maskOpaque} 52%, transparent 100%)`,
      }}
    >
      <img
        src="/cta-insurance-ai.png"
        alt=""
        className="absolute inset-0 h-full w-full scale-[1.04] object-cover object-center opacity-[0.34] saturate-[1.06] contrast-[0.88] blur-[0.6px] hue-rotate-[-60deg]"
        loading="lazy"
        decoding="async"
      />
      {/* Glass and palette block covering the full frame. */}
      <div className={`absolute inset-0 bg-gradient-to-br from-[color-mix(in_srgb,var(--brand-canvas-alt)_82%,transparent)] via-[color-mix(in_srgb,var(--brand-surface-tint)_58%,transparent)] to-[color-mix(in_srgb,var(--brand-canvas-alt)_72%,transparent)]`} />
      <div
        className="absolute inset-0 opacity-90"
        style={{
          background:
            `linear-gradient(135deg, ${brandRgba('accentLight', 0.24)} 0%, ${brandRgba('accentSoft', 0.18)} 35%, ${brandRgba('accent', 0.16)} 70%, ${brandRgba('surfaceTint', 0.22)} 100%)`,
        }}
      />
      <div className={`absolute inset-0 shadow-[inset_0_0_100px_rgba(var(--brand-rgb-surface-card),calc(var(--shadow-strength)*0.28))]`} />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* FOOTER                                                              */
/* ------------------------------------------------------------------ */
function Footer() {
  const { t } = useTranslation();

  return (
    <footer className="relative pb-10 pt-12">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-2 gap-8 md:grid-cols-6">
          <div className="col-span-2">
            <a href="/" className="flex items-center gap-2.5">
              <div className="grid h-9 w-9 place-items-center rounded-xl bg-gradient-to-br from-tone-accent-gradient-start to-tone-accent-gradient-end shadow-md">
                <brand.components.LogoGlyph className="h-5 w-5 stroke-[2.5] text-tone-on-accent" />
              </div>
              <div className="leading-tight">
                <div className="text-[15px] font-bold tracking-tight">
                  {t('landing:brand.name')}
                </div>
                <div className="text-[10px] font-medium text-tone-ink-muted">
                  {t('landing:brand.tagline')}
                </div>
              </div>
            </a>
          </div>

          <FooterColumn
            title={t('landing:footer.product')}
            items={[
              t('landing:footer.features'),
              t('landing:footer.personas'),
              t('landing:footer.solutions'),
              t('landing:footer.pricing'),
            ]}
          />
          <FooterColumn
            title={t('landing:footer.company')}
            items={[
              t('landing:footer.about'),
              t('landing:footer.careers'),
              t('landing:footer.blog'),
              t('landing:footer.contact'),
            ]}
          />
          <FooterColumn
            title={t('landing:footer.resources')}
            items={[
              t('landing:footer.helpCenter'),
              t('landing:footer.guides'),
              t('landing:footer.webinars'),
              t('landing:footer.community'),
            ]}
          />
          <div>
            <FooterColumn
              title={t('landing:footer.legal')}
              items={[
                t('landing:footer.privacyPolicy'),
                t('landing:footer.termsOfService'),
                t('landing:footer.security'),
              ]}
            />
            <div className="mt-6">
              <h4 className="mb-3 text-sm font-bold text-tone-ink-strong">
                {t('landing:footer.followUs', { defaultValue: 'Follow us' })}
              </h4>
              <div className="flex items-center gap-2">
                {[
                  { icon: Linkedin, label: 'LinkedIn' },
                  { icon: Facebook, label: 'Facebook' },
                  { icon: Instagram, label: 'Instagram' },
                  { icon: Twitter, label: 'Twitter' },
                ].map(({ icon: Icon, label }) => (
                  <a
                    key={label}
                    href="#"
                    aria-label={t(`landing:footer.social.${label.toLowerCase()}`, {
                      defaultValue: label,
                    })}
                    className="grid h-8 w-8 place-items-center rounded-full border border-tone-glass/70 bg-tone-glass/70 text-tone-ink-secondary shadow-sm backdrop-blur-md transition-all hover:-translate-y-0.5 hover:bg-gradient-to-br hover:from-tone-accent-gradient-start hover:to-tone-accent-gradient-end hover:text-tone-on-accent"
                  >
                    <Icon className="h-3.5 w-3.5" />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-10 flex flex-col items-center justify-between gap-4 border-t border-tone-glass/60 pt-5 sm:flex-row">
          <p className="text-xs text-tone-ink-muted">
            {t('landing:footer.copyright', { year: new Date().getFullYear() })}
          </p>
          <FooterLanguageSwitcher />
        </div>
      </div>
    </footer>
  );
}

/**
 * Landing footer language switcher — glass pill matching the landing aesthetic.
 * Data-driven (lists every language from `availableLanguages`) and shown only when
 * there is more than one to choose from.
 */
function FooterLanguageSwitcher() {
  const { t } = useTranslation();
  const { language, availableLanguages } = useLanguage();
  const switchLang = useLanguageSwitch();

  if (availableLanguages.length < 2) return null;

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={t('common:a11y.changeLanguage')}
          className="inline-flex h-8 items-center gap-2 rounded-full border border-tone-glass/70 bg-tone-glass/70 px-3 text-xs font-medium text-tone-ink-secondary shadow-sm backdrop-blur-md transition-all hover:-translate-y-0.5 hover:text-tone-accent-ink"
        >
          <Globe className="h-3.5 w-3.5" />
          <span>{getLanguageInfo(language).nativeName}</span>
          <ChevronDown className="h-3 w-3 opacity-70" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        side="top"
        className="min-w-[180px] rounded-xl border border-tone-glass/70 bg-tone-glass/90 backdrop-blur-md"
      >
        {availableLanguages.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => {
              if (lang.code !== language)
                void switchLang(lang.code).catch((e) =>
                  console.error('Language switch failed:', e)
                );
            }}
            className={cn(
              'cursor-pointer gap-2 text-tone-ink-secondary focus:text-tone-accent-ink-deep',
              lang.code === language && 'text-tone-accent-ink-deep'
            )}
          >
            <span className="w-6 text-[10px] font-bold uppercase tracking-wider text-tone-ink-faint">
              {lang.code}
            </span>
            <span className="flex-1">{lang.nativeName}</span>
            {lang.code === language && <Check className="h-3.5 w-3.5 text-tone-accent-ink" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function FooterColumn({ title, items }: { title: string; items: string[] }) {
  return (
    <div>
      <h4 className="mb-3 text-sm font-bold text-tone-ink-strong">{title}</h4>
      <ul className="space-y-2 text-sm text-tone-ink-muted">
        {items.map((item) => (
          <li key={item}>
            <a href="#" className="transition-colors hover:text-tone-accent-ink">
              {item}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* SHARED: Glass & Gradient Buttons                                    */
/* ------------------------------------------------------------------ */
function GlassButton({
  children,
  className,
  onClick,
}: {
  children: ReactNode;
  className?: string;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        `group relative inline-flex items-center justify-center overflow-hidden rounded-full font-semibold text-[var(--brand-ink)]`,
        'border border-tone-glass/50 backdrop-blur-[20px] backdrop-saturate-[1.55]',
        `shadow-[0_10px_28px_-10px_rgba(var(--brand-rgb-accent),calc(var(--shadow-strength)*0.28)),inset_0_1px_0_rgba(var(--brand-rgb-surface-card),calc(var(--shadow-strength)*0.95)),inset_0_calc(-1px)_0_rgba(var(--brand-rgb-surface-card),calc(var(--shadow-strength)*0.2))]`,
        'transition-all hover:-translate-y-0.5 hover:border-tone-glass/65 hover:bg-tone-glass/40',
        `hover:shadow-[0_14px_32px_-10px_rgba(var(--brand-rgb-accent),calc(var(--shadow-strength)*0.35)),inset_0_1px_0_rgba(var(--brand-rgb-surface-card),calc(var(--shadow-strength)*1))]`,
        className
      )}
    >
      {/* Top mirror sheen */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-x-2 top-0.5 h-[52%] rounded-full bg-gradient-to-b from-tone-glass/60 via-tone-glass/25 to-transparent opacity-90"
      />
      {/* Bottom soft reflection */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0 h-[35%] rounded-b-full bg-gradient-to-t to-transparent"
      />
      <span className="relative z-10 inline-flex items-center">{children}</span>
    </button>
  );
}

function GradientButton({
  children,
  className,
  onClick,
}: {
  children: ReactNode;
  className?: string;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        `group relative inline-flex items-center justify-center overflow-hidden rounded-full font-semibold text-tone-on-accent shadow-[0_10px_28px_-8px_rgba(var(--brand-rgb-accent-mid),calc(var(--shadow-strength)*0.6))] transition-all hover:-translate-y-0.5 hover:shadow-[0_18px_36px_-8px_rgba(var(--brand-rgb-accent-mid),calc(var(--shadow-strength)*0.75))]`,
        className
      )}
      style={{
        background: landingGradients.brandButtonInline,
      }}
    >
      {/* Inner gloss */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-x-2 top-0.5 h-1/2 rounded-full opacity-60"
        style={{
          background: `linear-gradient(to bottom, ${brandRgba('surfaceCard', 0.45)}, ${brandRgba('surfaceCard', 0)})`,
        }}
      />
      {/* Hover lift gradient */}
      <span
        aria-hidden
        className="absolute inset-0 opacity-0 transition-opacity group-hover:opacity-100"
        style={{
          background: `linear-gradient(95deg, ${brandColors.accentLight} 0%, ${brandColors.accentSoft} 55%, ${brandColors.accentDeep} 100%)`,
        }}
      />
      <span className="relative z-10 inline-flex items-center gap-1.5">{children}</span>
    </button>
  );
}

export default LandingPage;
